import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import {
  defaultOpenSeasonIds,
  buildSeasonFilterOptions,
  seasonFilterPlaceholder,
  loadSeasons,
  resolveSeasonId,
} from "./seasonFilter";
import { loadFromSupabase, saveToSupabase, deleteFromSupabase } from "../supabaseDB";
import { matchLineToInvoiceLines, deriveDeliveryStatus, resolvePendingStage, buildSlPassingIndex, PENDING_STATUSES } from "./saleStatusCalc";

/* ============================================================
   FixedPricingRegistry — Client Pricing & POs · Tab 1
   ------------------------------------------------------------
   Tracks fixed-price client POs (Four Seasons today) line by
   line. Each row = one product line within a PO. The captured
   prices (FS / SL / GF / Grower + commission %) feed three
   derived profit columns and a total. When an Invoice updates
   the FS price later, `priceFSReal` is filled and the doc
   surfaces both Estimated and Real profit for comparison.

   Storage:   localStorage `gfi-fixed-pos`
   Window globals (for cross-module integration):
     - window.__gfiGetMatchingPOs(date, clientId)
         Returns up to 5 PO lines whose date is within ±7 days
         of the requested date. Used by the Invoice form to
         suggest a PO when creating a new invoice.
   ============================================================ */

const T = {
  en: {
    title: "Weekly PO's", subtitle: "Per-line client POs — Fixed, Semi-fixed, and Consignment terms — with live profit tracking",
    add: "+ New PO Line", edit: "Edit PO line", view: "View PO line",
    search: "Search by PO / PU / Logger / Product...", noResults: "No PO lines match the filters.",
    /* Summary cards */
    cardLines: "PO Lines", cardCases: "Total Cases", cardRevenue: "Total Revenue (FS)", cardProfit: "Total Profit (Est.)",
    cardPending: "Pending Invoice", cardSettled: "Settled",
    /* Columns */
    colDate: "Departure\nDate", colDelivery: "Loading/\nDelivery Date", colWeek: "Wk", colClient: "Client", colPO: "PO #", colPU: "PU #", colLogger: "Logger #",
    colProduct: "Product", colCat: "CAT",
    colPalletsSent: "Pallets\nSent", colCasesSent: "Cases\nSent",
    colPalletsRcv: "Pallets\nRcv", colCasesRcv: "Cases\nRcv",
    colPriceFS: "Price\nFS", colPriceSL: "Price\nSL", colPriceGF: "Price\nGF", colPriceGrower: "Price\nGrower",
    colPriceFSReal: "FS\nReal",
    colCommPct: "Comm\n%",
    colProfitSL: "SL\nProfit", colProfitDiff: "Diff\nProfit", colProfitComm: "Comm\nProfit", colProfitFinance: "Finance\nProfit", colProfitTotal: "Total\nProfit",
    colProfitReal: "Real\nProfit",
    colPoStatus: "PO\nStatus", colArrivalStatus: "Arrival\nStatus", colSaleStatus: "Sale\nStatus",
    /* Form sections */
    secHeader: "PO Header", secProduct: "Product", secQty: "Quantities", secPricing: "Pricing", secMeta: "Status & Notes",
    /* Form fields */
    date: "Departure Date", deliveryDate: "Loading/Delivery Date",
    departureHint: "Auto-calculated as 2 days before delivery — editable",
    client: "Client", selectClient: "Select client...",
    poNumber: "PO #", puNumber: "PU #", loggerNumber: "Logger #",
    product: "Product", selectProduct: "Select product...",
    category: "Category",
    palletsSent: "Pallets", casesSent: "Cases",
    palletsReceived: "Pallets", casesReceived: "Cases",
    colGroupOrdered: "Ordered", colGroupReceived: "Received", addProduct: "Add Product", removeProduct: "Remove product",
    customerCol: "Customer", slCol: "SL", gfCol: "GF", growerCol: "Grower", rowPrice: "Price", rowAfterSale: "After Sale Price",
    firmPriceGroup: "Firm Price", afterSalePriceGroup: "After Sale Price",
    commHint: "The GFI commission rate applied to this line's profit calc (default 5%, editable per product). If the real rate ends up different once the sale settles, just adjust this same field.",
    secTerm: "Term", term: "Term", termFixed: "Fixed", termSemifixed: "Semi-fixed", termConsignment: "Consignment",
    priceFSMin: "Min Price\n(Customer)", priceFSMax: "Max Price\n(Customer)", priceFSMidpoint: "Midpoint\n(used for calc)",
    /* Consignment (Phase 2, 2026-09-18) — TR = Twin River, the price
       is a RANGE the client sets, not a single number Arturo picks. */
    trPriceGroup: "TR Price Range", trRealGroup: "Real (TR)",
    minTR: "Min\n(TR)", maxTR: "Max\n(TR)", commTR: "Comm %\n(TR)", differential: "Differential", commGFI: "Comm %\n(GFI)", advance: "Advance",
    midTRCol: "Mid TR", midGFCol: "Mid GF", midGrowerCol: "Mid Grower",
    realTR: "Real TR", realCommTR: "Real Comm %\n(TR)", realDifferential: "Real\nDifferential", realCommGFI: "Real Comm %\n(GFI)",
    realGFCol: "Real GF", realGrowerCol: "Real Grower",
    secQtyOrdered: "Ordered", secQtyActual: "Actual (auto-filled from Invoice)",
    priceFS: "Price FS (paid by client)", priceSL: "Price SL (SweetLeaf charges)",
    priceGF: "Price GF (GFI retains)", priceGrower: "Price Grower (reported to exporter)",
    priceFSReal: "FS Real Price (from invoice)", priceFSRealHint: "Auto-filled when the linked invoice updates the FS price.",
    priceSLReal: "Real SL Price", priceSLRealHint: "Real SL price after sale. Empty = use estimated.",
    priceGFReal: "Real GF Price", priceGFRealHint: "Real GF price GFI actually retained. Empty = use estimated.",
    priceGrowerReal: "Real Grower Price", priceGrowerRealHint: "Real price reported to exporter. Empty = use estimated.",
    commissionPctReal: "Real Commission %", commissionPctRealHint: "Real commission %. Use 0 if waived. Empty = use estimated.",
    financeFeePaidReal: "Real Finance Paid $", financeFeePaidRealHint: "Real $ paid to factoring. Empty = use estimated.",
    financeFeeChargedReal: "Real Finance Charged $", financeFeeChargedRealHint: "Real $ charged for factoring. Empty = use estimated.",
    colPriceSLReal: "SL\nReal", colPriceGFReal: "GF\nReal", colPriceGrowerReal: "Grower\nReal",
    colCommPctReal: "Comm\nReal", colFinPaidReal: "Fin Paid\nReal", colFinChargedReal: "Fin Chg\nReal",
    secReal: "After-Sale (Real Numbers)",
    realSummary: "Real Profit Summary",
    commissionPct: "Commission %",
    financeFeePaid: "Finance Paid $", financeFeeCharged: "Finance Charged $",
    poStatus: "PO Status", arrivalStatus: "Arrival Status",
    saleStatus: "Sale Status", selectStatus: "Select status...", selectSettlement: "Select...",
    linkedInvoice: "Linked Invoice", noLinkedInvoice: "No invoice linked",
    notes: "Notes", notesPlaceholder: "Internal notes...",
    /* Profit summary */
    profitSummary: "Estimated Profit Summary",
    profitSLDesc: "SweetLeaf spread (FS − SL) × cases",
    profitDiffDesc: "Differential (GF − Grower) × cases",
    profitCommDesc: "Commission (Grower × Comm%) × cases",
    profitFinanceDesc: "Finance markup (Charged − Paid)",
    profitTotalDesc: "Total estimated profit",
    profitRealLabel: "Real Profit (using FS Real price):",
    /* Status options — 3 independent axes (2026-09-18, Arturo):
       PO Status (statusPending/statusInvoiced, computed), Arrival
       Status (statusPendingPickUp..statusTransfer, computed, mirrors
       the linked invoice), Sale Status (statusPaidFull..
       statusDumpedNoPay, manual — financial settlement). */
    statusPending: "Pending Invoice", statusInvoiced: "Invoiced",
    statusPendingPickUp: "Pending Pick Up", statusPendingDelivery: "Pending Delivery",
    statusGoodDelivery: "Good Delivery", statusProtection: "Protection", statusPAS: "PAS", statusPartialProtection: "Partial Protection",
    statusDumped: "Dumped", statusDonated: "Donated", statusSamples: "Samples", statusTransfer: "Transfer",
    statusPaidFull: "Paid in Full", statusSettled: "Settled",
    statusDonatedNoPay: "Donated (nothing paid)", statusDumpedNoPay: "Dumped (nothing paid)",
    /* Filters */
    allClients: "All clients", allStatuses: "All statuses",
    /* Actions */
    save: "Save", saveAndNew: "Save & New", cancel: "Cancel", del: "Delete",
    editBtn: "Edit", closeBtn: "Close",
    confirmDel: "Delete this PO line? This cannot be undone.",
    confirmExit: "Unsaved changes. Exit anyway?", yesExit: "Yes, exit", noExit: "Keep editing",
    /* Validation */
    fieldRequired: "Required",
    noDate: "Date is required.",
    noPO: "PO # is required.",
    noProduct: "Product is required.",
    noPriceFS: "FS price is required.",
    noCases: "Cases sent is required.",
    /* Toasts */
    saved: "PO line saved", deleted: "PO line deleted",
    /* Header */
    clearForm: "Clear Form", confirmClear: "Clear all form fields?",
    weekShort: "W",
  },
  es: {
    title: "PO's Semanales", subtitle: "POs de cliente línea por línea — términos Fixed, Semi-fixed y Consignment — con seguimiento de ganancia en vivo",
    add: "+ Nueva Línea de PO", edit: "Editar línea de PO", view: "Ver línea de PO",
    search: "Buscar por PO / PU / Logger / Producto...", noResults: "Ninguna línea coincide con los filtros.",
    cardLines: "Líneas PO", cardCases: "Cajas Totales", cardRevenue: "Venta Total (FS)", cardProfit: "Ganancia Total (Est.)",
    cardPending: "Pendiente Factura", cardSettled: "Liquidado",
    colDate: "Fecha\nSalida", colDelivery: "Carga/\nEntrega", colWeek: "Sem", colClient: "Cliente", colPO: "PO #", colPU: "PU #", colLogger: "Logger #",
    colProduct: "Producto", colCat: "CAT",
    colPalletsSent: "Pallets\nEnv.", colCasesSent: "Cajas\nEnv.",
    colPalletsRcv: "Pallets\nRec.", colCasesRcv: "Cajas\nRec.",
    colPriceFS: "Precio\nFS", colPriceSL: "Precio\nSL", colPriceGF: "Precio\nGF", colPriceGrower: "Precio\nExp.",
    colPriceFSReal: "FS\nReal",
    colCommPct: "Com\n%",
    colProfitSL: "Gan.\nSL", colProfitDiff: "Gan.\nDif.", colProfitComm: "Gan.\nCom.", colProfitFinance: "Gan.\nFin.", colProfitTotal: "Gan.\nTotal",
    colProfitReal: "Gan.\nReal",
    colPoStatus: "Status\nPO", colArrivalStatus: "Status\nLlegada", colSaleStatus: "Status\nVenta",
    secHeader: "Encabezado PO", secProduct: "Producto", secQty: "Cantidades", secPricing: "Precios", secMeta: "Estado y Notas",
    date: "Fecha de Salida", deliveryDate: "Fecha de Carga/Entrega",
    departureHint: "Se auto-calcula 2 días antes de la entrega — editable",
    client: "Cliente", selectClient: "Seleccionar cliente...",
    poNumber: "PO #", puNumber: "PU #", loggerNumber: "Logger #",
    product: "Producto", selectProduct: "Seleccionar producto...",
    category: "Categoría",
    palletsSent: "Pallets", casesSent: "Cajas",
    palletsReceived: "Pallets", casesReceived: "Cajas",
    colGroupOrdered: "Pedido", colGroupReceived: "Recibido", addProduct: "Agregar Producto", removeProduct: "Quitar producto",
    customerCol: "Cliente", slCol: "SL", gfCol: "GF", growerCol: "Exportador", rowPrice: "Precio", rowAfterSale: "Precio Post-Venta",
    firmPriceGroup: "Firm Price", afterSalePriceGroup: "After Sale Price",
    commHint: "La tasa de comisión GFI que se usa en el cálculo de ganancia de esta línea (default 5%, editable por producto). Si la tasa real resulta distinta al liquidar la venta, ajusta este mismo campo.",
    secTerm: "Term", term: "Term", termFixed: "Fixed", termSemifixed: "Semi-fixed", termConsignment: "Consignment",
    priceFSMin: "Precio Mín\n(Cliente)", priceFSMax: "Precio Máx\n(Cliente)", priceFSMidpoint: "Promedio\n(usado para cálculo)",
    trPriceGroup: "Rango de Precio (TR)", trRealGroup: "Real (TR)",
    minTR: "Mín\n(TR)", maxTR: "Máx\n(TR)", commTR: "Com %\n(TR)", differential: "Diferencial", commGFI: "Com %\n(GFI)", advance: "Adelanto",
    midTRCol: "Prom TR", midGFCol: "Prom GF", midGrowerCol: "Prom Exportador",
    realTR: "TR Real", realCommTR: "Com % Real\n(TR)", realDifferential: "Diferencial\nReal", realCommGFI: "Com % Real\n(GFI)",
    realGFCol: "GF Real", realGrowerCol: "Exportador Real",
    secQtyOrdered: "Pedido", secQtyActual: "Real (se llena solo desde el Invoice)",
    priceFS: "Precio FS (paga el cliente)", priceSL: "Precio SL (cobra SweetLeaf)",
    priceGF: "Precio GF (retiene GFI)", priceGrower: "Precio Exportador (reportado)",
    priceFSReal: "Precio FS Real (de la factura)", priceFSRealHint: "Se llena automáticamente cuando la factura vinculada modifica el precio FS.",
    priceSLReal: "Precio SL Real", priceSLRealHint: "Precio SL real post-venta. Vacío = usa el estimado.",
    priceGFReal: "Precio GF Real", priceGFRealHint: "Precio GF real que GFI retuvo. Vacío = usa el estimado.",
    priceGrowerReal: "Precio Exportador Real", priceGrowerRealHint: "Precio real reportado al exportador. Vacío = usa el estimado.",
    commissionPctReal: "Comisión Real %", commissionPctRealHint: "Comisión real %. Usa 0 si la removiste. Vacío = usa el estimado.",
    financeFeePaidReal: "Financiamiento Pagado Real $", financeFeePaidRealHint: "$ real pagado al factoraje. Vacío = usa el estimado.",
    financeFeeChargedReal: "Financiamiento Cobrado Real $", financeFeeChargedRealHint: "$ real cobrado por factoraje. Vacío = usa el estimado.",
    colPriceSLReal: "SL\nReal", colPriceGFReal: "GF\nReal", colPriceGrowerReal: "Exp.\nReal",
    colCommPctReal: "Com\nReal", colFinPaidReal: "Fin Pag\nReal", colFinChargedReal: "Fin Cob\nReal",
    secReal: "Post-Venta (Números Reales)",
    realSummary: "Resumen de Ganancia Real",
    commissionPct: "Comisión %",
    financeFeePaid: "Financiamiento Pagado $", financeFeeCharged: "Financiamiento Cobrado $",
    poStatus: "Status de PO", arrivalStatus: "Status de Llegada",
    saleStatus: "Status de Venta", selectStatus: "Seleccionar estado...", selectSettlement: "Seleccionar...",
    linkedInvoice: "Factura Vinculada", noLinkedInvoice: "Sin factura vinculada",
    notes: "Notas", notesPlaceholder: "Notas internas...",
    profitSummary: "Resumen de Ganancia Estimada",
    profitSLDesc: "Spread SweetLeaf (FS − SL) × cajas",
    profitDiffDesc: "Diferencial (GF − Exportador) × cajas",
    profitCommDesc: "Comisión (Exportador × Com%) × cajas",
    profitFinanceDesc: "Markup financiero (Cobrado − Pagado)",
    profitTotalDesc: "Ganancia total estimada",
    profitRealLabel: "Ganancia Real (con precio FS Real):",
    statusPending: "Pendiente Factura", statusInvoiced: "Facturado",
    statusPendingPickUp: "Pendiente Recolección", statusPendingDelivery: "Pendiente Entrega",
    statusGoodDelivery: "Good Delivery", statusProtection: "Protección", statusPAS: "PAS", statusPartialProtection: "Protección Parcial",
    statusDumped: "Dumped", statusDonated: "Donated", statusSamples: "Samples", statusTransfer: "Transferencia",
    statusPaidFull: "Pago Completo", statusSettled: "Liquidado",
    statusDonatedNoPay: "Donado (sin pago)", statusDumpedNoPay: "Tirado (sin pago)",
    allClients: "Todos los clientes", allStatuses: "Todos los estados",
    save: "Guardar", saveAndNew: "Guardar y Nuevo", cancel: "Cancelar", del: "Eliminar",
    editBtn: "Editar", closeBtn: "Cerrar",
    confirmDel: "¿Eliminar esta línea de PO? No se puede deshacer.",
    confirmExit: "Cambios sin guardar. ¿Salir de todos modos?", yesExit: "Sí, salir", noExit: "Seguir editando",
    fieldRequired: "Requerido",
    noDate: "La fecha es requerida.",
    noPO: "El PO # es requerido.",
    noProduct: "El producto es requerido.",
    noPriceFS: "El precio FS es requerido.",
    noCases: "Las cajas enviadas son requeridas.",
    saved: "Línea de PO guardada", deleted: "Línea de PO eliminada",
    clearForm: "Limpiar Formulario", confirmClear: "¿Limpiar todos los campos?",
    weekShort: "Sem",
  },
};

/* ---------- Constants ---------- */
const STORAGE_KEY = "gfi-fixed-pos";
/* Legacy localStorage mirror for Consignment rows now captured from
   this same screen (Phase 2) — matches the key
   ConsignmentPricingRegistry.js has always used, so any legacy
   consumer of that specific key keeps working whether a Consignment
   PO was captured there or here. */
const CONSIGNMENT_STORAGE_KEY = "gfi-consignment-pos";
const DEFAULT_COMMISSION = 5;
const TERM_OPTIONS = ["fixed", "semifixed", "consignment"];
/* tableForTerm — every PO/line's storage table is derived from its
   OWN `term`, never a separate toggle (Phase 2, 2026-09-18): Fixed
   and Semi-fixed both live in fixed_pos (Semi-fixed's range collapses
   to a single priceFS midpoint on save, per the Phase 1 comment
   above), Consignment lives in consignment_pos, matching where
   ConsignmentPricingRegistry.js already reads/writes. */
function tableForTerm(term) { return term === "consignment" ? "consignment_pos" : "fixed_pos"; }
/* Same "sweetleaf"/"four seasons" substring check already used
   elsewhere in this app (emailHelpers.js's isFourSeasonsClientName) —
   duplicated here per this repo's per-file convention. SweetLeaf is
   the only client relationship that needs a 4th price (Price SL, for
   SL's own profit calc) — everyone else only sees Customer/GF/Grower. */
function isSweetLeafClient(name) {
  var n = String(name || "").toLowerCase();
  return n.indexOf("sweetleaf") >= 0 || n.indexOf("sweet leaf") >= 0 || n.indexOf("four seasons") >= 0;
}
/* Kept as the full badge-color/label palette shared by all 3 status
   axes below (PO Status, Arrival Status, Sale/Settlement Status) —
   StatusBadge looks up any of these by value regardless of which
   field it's rendering. SALE_STATUSES (the outer table's status
   filter dropdown) only lists the PO-Status values now — Arrival and
   Settlement get their own dedicated pickers/badges, not this shared
   filter (2026-09-18, Arturo's 3-status split). */
const SALE_STATUSES = ["pending", "invoiced"];
/* Arrival Status — computed live from the linked invoice, never
   persisted (see computeLiveStatuses below). Exported list mainly so
   badge/label lookups have a single source of truth. */
const ARRIVAL_STATUSES = ["pendingPickUp", "pendingDelivery", "goodDelivery", "protection", "pas", "partialProtection", "dumped", "donated", "samples", "transfer"];
/* Sale Status (2026-09-18, Arturo) — manual, financial-settlement
   axis, independent of the fruit-delivery-condition dispositions that
   share the same words on Arrival Status (a pallet "dumped" ≠ a PO
   settled as "nothing paid because dumped") — separate keys to avoid
   conflating the two meanings. */
const SETTLEMENT_STATUSES = ["paidFull", "settled", "donatedNoPay", "dumpedNoPay"];
const STATUS_COLORS = {
  pending:      { bg: "rgba(156,163,175,0.15)", text: "#6B7280", border: "rgba(156,163,175,0.3)" },
  invoiced:     { bg: "rgba(59,130,246,0.15)",  text: "#2563EB", border: "rgba(59,130,246,0.3)" },
  pendingPickUp:   { bg: "rgba(156,163,175,0.15)", text: "#6B7280", border: "rgba(156,163,175,0.3)" },
  pendingDelivery: { bg: "rgba(59,130,246,0.12)",  text: "#1D4ED8", border: "rgba(59,130,246,0.3)" },
  goodDelivery: { bg: "rgba(34,197,94,0.15)",   text: "#15803D", border: "rgba(34,197,94,0.3)" },
  protection:   { bg: "rgba(245,158,11,0.15)",  text: "#B45309", border: "rgba(245,158,11,0.3)" },
  pas:          { bg: "rgba(217,70,239,0.15)",  text: "#A21CAF", border: "rgba(217,70,239,0.3)" },
  partialProtection: { bg: "rgba(245,158,11,0.15)", text: "#B45309", border: "rgba(245,158,11,0.3)" },
  dumped:       { bg: "rgba(220,38,38,0.15)",   text: "#B91C1C", border: "rgba(220,38,38,0.3)" },
  donated:      { bg: "rgba(20,184,166,0.15)",  text: "#0F766E", border: "rgba(20,184,166,0.3)" },
  samples:      { bg: "rgba(168,85,247,0.15)",  text: "#7E22CE", border: "rgba(168,85,247,0.3)" },
  transfer:     { bg: "rgba(59,130,246,0.15)",  text: "#2563EB", border: "rgba(59,130,246,0.3)" },
  paidFull:     { bg: "rgba(16,185,129,0.15)",  text: "#047857", border: "rgba(16,185,129,0.3)" },
  settled:      { bg: "rgba(249,115,22,0.15)",  text: "#C2410C", border: "rgba(249,115,22,0.35)" },
  donatedNoPay: { bg: "rgba(20,184,166,0.15)",  text: "#0F766E", border: "rgba(20,184,166,0.3)" },
  dumpedNoPay:  { bg: "rgba(220,38,38,0.15)",   text: "#B91C1C", border: "rgba(220,38,38,0.3)" },
};

/* Live status computation (2026-09-18, Arturo) — PO Status ("Pending
   Invoice"/"Invoiced") and Arrival Status (Pending Pick Up/Delivery,
   Good Delivery, Protection, PAS, dispositions...) are NEVER manually
   set here — both are derived fresh from whatever the linked
   invoice(s) currently say, same "always computed, never a frozen
   snapshot" rule saleStatusCalc.js already establishes for pallets/
   Bill/Shipment (REGLA DE STATUS DE PALLET). invoicesById: map of
   invoice.id -> invoice. slPassingIndex: from buildSlPassingIndex().
   Returns { poStatus, arrivalStatus } — arrivalStatus is "" when the
   PO isn't linked to any invoice yet (nothing to derive). */
function computeLiveStatuses(po, invoicesById, slPassingIndex) {
  var invoice = po && po.linkedInvoiceId ? invoicesById[String(po.linkedInvoiceId)] : null;
  if (!invoice) return { poStatus: "pending", arrivalStatus: "" };
  /* Match by product/category (matchLineToInvoiceLines), then mix the
     matched invoice lines' own saleStatus the same way pallets/Bill/
     Shipment already do (deriveDeliveryStatus). Deliberately NOT
     using saleStatusCalc.js's deriveBillLineLiveStatus here — its
     no-match fallback returns the PASSED-IN line's OWN saleStatus,
     which for a Bill line is a real delivery-condition value but for
     a PO record is now "pending"/"invoiced" (PO Status) — that
     fallback would leak "Invoiced" into Arrival Status on a match
     miss instead of leaving it blank/pending. */
  var matched = matchLineToInvoiceLines(po, invoice.lines || []);
  var live = matched.length ? deriveDeliveryStatus(matched) : null;
  if (!live || PENDING_STATUSES.indexOf(live) !== -1) {
    live = resolvePendingStage([invoice], slPassingIndex) || "";
  }
  return { poStatus: "invoiced", arrivalStatus: live };
}

/* Customer After-Sale Real price (2026-09-18, Arturo — this section's
   whole feature): "el after sale price se puede popular de las ventas
   reales, cuando es sl busca la venta en las facturas de sweetleaf en
   su modulo, todo GF busca las ventas en las facturas de venta de gf"
   — only ever a SOURCE for priceFSReal (the one field with an
   unambiguous 1:1 real number, an invoice line's own `salePrice`); SL/
   GF/Grower Real stay manual (no single invoice field maps to those
   split tiers without guessing a formula — explicitly left alone,
   same caution Arturo already applied to Grower himself).

   Gated on Good Delivery ONLY (Arturo, same breath: "cuando genero
   una factura se adjudica precio de venta pero si cambia a status de
   protection o PAS, el after sale price debe desaparecer y esperar la
   venta final") — a Protection/PAS/still-pending line's sale price
   isn't final yet, so this never surfaces a number for those; it
   simply returns null and the caller leaves whatever's already saved
   untouched (never destructively clears a value Arturo may have
   entered on purpose — see enriched() below). */
function resolveRealCustomerPrice(po, arrivalStatus, invoicesById, slInvoicesRaw) {
  if (arrivalStatus !== "goodDelivery") return null;
  if (!po || !po.linkedInvoiceId) return null;
  var invoice = invoicesById[String(po.linkedInvoiceId)];
  if (!invoice) return null;
  var sourceLines = invoice.lines || [];
  if (isSweetLeafClient(po.clientName)) {
    var slInvoice = (slInvoicesRaw || []).find(function (s) { return String(s.gfiInvoiceId) === String(invoice.id); });
    if (!slInvoice) return null;
    sourceLines = slInvoice.lines || [];
  }
  var matched = matchLineToInvoiceLines(po, sourceLines);
  var line = matched[0];
  if (!line || line.salePrice === "" || line.salePrice == null) return null;
  return line.salePrice;
}

const EMPTY = {
  id: "", createdAt: "", updatedAt: "",
  date: "", week: "", deliveryDate: "",
  clientId: "", clientName: "",
  poNumber: "", puNumber: "", loggerNumber: "",
  /* term: "fixed" | "semifixed" | "consignment" — Arturo, 2026-09-18:
     unifies what used to be 2 separate screens (Fixed Pricing vs.
     Consignment Pricing) into one. Semi-fixed is a range with NO
     client commission — the single price used everywhere else
     (priceFS, profit calc, table columns) is just the midpoint of
     priceFSMin/priceFSMax, computed on save so nothing downstream
     needs to know the term exists. Consignment (full range + client
     commission) keeps living in the separate `consignment_pos` table
     — Phase 2 of the merge (this same edit): routed by `tableForTerm`
     at save/delete time, same record shape/fields as the original
     ConsignmentPricingRegistry.js so weeklyWeightedPrices.js and
     InvoiceRegistry.js's syncPoLinkage (already table-agnostic) keep
     working unchanged. */
  term: "fixed",
  priceFSMin: "", priceFSMax: "",
  productId: "", productBase: "", productName: "", productDescription: "", category: "",
  palletsSent: "", casesSent: "",
  palletsReceived: "", casesReceived: "",
  priceFS: "", priceSL: "", priceGF: "", priceGrower: "",
  priceFSReal: "",
  priceSLReal: "", priceGFReal: "", priceGrowerReal: "",
  commissionPct: DEFAULT_COMMISSION,
  commissionPctReal: "",
  /* Consignment-only fields (term === "consignment") — exact same
     names as ConsignmentPricingRegistry.js's own EMPTY, so a record
     saved here is indistinguishable from one saved there. TR = Twin
     River, the main consignment client. pallets/cases here (not
     palletsSent/casesSent) mirror the original screen's own line
     shape exactly — weeklyWeightedPrices.js reads `row.cases`
     directly. */
  minTR: "", maxTR: "", commTR: "10", realCommTR: "",
  differential: "", realDifferential: "",
  commGFI: DEFAULT_COMMISSION, realCommGFI: "",
  advance: "",
  priceEstTR: "", priceRealTR: "",
  pallets: "", cases: "",
  /* Finance markup, captured as DOLLAR AMOUNTS per invoice.
     financeFeePaid    = $ SweetLeaf pays AGROCAP for this invoice
     financeFeeCharged = $ SweetLeaf charges Ground-Fresh for this
                         invoice. The straight difference (Charged
                         − Paid) rolls into Total Profit as a 4th
                         component (profitFinance) — no scaling by
                         cases / invoice value. */
  financeFeePaid: "",
  financeFeeCharged: "",
  financeFeePaidReal: "",
  financeFeeChargedReal: "",
  saleStatus: "pending",
  /* settlementStatus (2026-09-18, Arturo) — a THIRD status, manual,
     entirely separate from saleStatus (PO Status, computed) and
     arrivalStatus (computed, not persisted): how the PO's money got
     resolved — Paid in Full / Settled / Donated (nothing paid) /
     Dumped (nothing paid). Never touched by syncPoLinkage or the
     live-status computation, always Arturo's own pick. */
  settlementStatus: "",
  linkedInvoiceId: "",
  seasonId: "",
  notes: "",
};

const inp = { padding: "7px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
const errS = { borderColor: "#DC2626", background: "#FFF8E1" };
/* Read-only "populated from elsewhere" fields (Week, PU#, Category,
   Midpoint...) are never typed by Arturo — they're either auto-derived
   or filled once linked to an Invoice. 2026-09-18, Arturo: "los campos
   que no se capturan si no que se populan de otro entorno... remueve
   el background y rectangulos que tengan" — same treatment as the
   PO/Arrival Status badges: plain text, no input-box border/background,
   just enough vertical padding to keep row height lined up with the
   real (editable) inputs beside them.
   Pallets/Cases Received used to live in this same read-only group
   (auto-filled once from the linked invoice, Phase 3) but Arturo asked
   later the same day to make them editable again: "los received cases
   debo poder editarlosd manualmente ahora uno no checa porque se
   perdieron 2 cajas y no se actyualizao" (PU 17440) — the auto-fill
   only ever seeds an empty field once, Arturo needs to correct it by
   hand when the real count differs (short shipment, damage, etc.), so
   they're now ordinary capture inputs (`capBg`/`gfi-num-input`) like
   Pallets/Cases Sent right next to them. */
const roInp = { ...inp, border: "none", background: "transparent", padding: "7px 4px" };
/* Yellow "still needs a value" highlight for CAPTURE fields — the
   ones Arturo actually types/picks (dates, client, PO#, quantities,
   prices, commission, Sale Status...), as opposed to the read-only
   `roInp` fields above. 2026-09-18, Arturo corrected this the same
   day it shipped ("debe ser amarillo al estar vacios no al estar
   llenos" / "lo tienes alreves") — amber flags an EMPTY field that's
   pending capture, same direction as the After-Sale Real fields'
   `realBg` below; it clears back to normal once a value is entered,
   not the other way around. */
var CAP_BG = "rgba(245,158,11,0.08)";
function capBg(value) { return (value === "" || value == null) ? CAP_BG : inp.background; }
/* After-Sale Real fields (Customer/SL/GF/Grower Real) use the OPPOSITE
   convention (2026-09-18, Arturo: "after sale price queda amarillo
   hasta que se captura y/o se popule") — amber while still empty/
   pending (a visual "needs a final number" flag), green once it has
   one, whether Arturo typed it or it got auto-filled from the real
   sale (see resolveRealCustomerPrice above). */
function realBg(value) { return (value !== "" && value != null) ? "rgba(16,185,129,0.06)" : CAP_BG; }
/* Force 2 decimals on every price field, on blur (2026-09-18, Arturo:
   "agrega 2 decimales a todos los precios por default, si escrivo 24
   quye muestre 24.00") — reformats onBlur, not onChange, so typing
   "24.5" doesn't get clobbered mid-keystroke; leaves an in-progress
   invalid/empty value alone rather than forcing "0.00" on it. */
function fmt2(v) { var n = parseFloat(v); return isNaN(n) ? v : n.toFixed(2); }
function priceBlur(e, key, set) {
  var v = e.target.value;
  if (v === "" || v == null) return;
  var n = parseFloat(v);
  if (isNaN(n)) return;
  set(key, n.toFixed(2));
}
/* Every money field in the Product table (2026-09-18, Arturo: "dije
   todos los campos de dinero no solo after sale price, incluye firm
   price") — `priceBlur` above only reformats a field AFTER Arturo
   touches it; this covers the OTHER half — a line already carrying
   "25.5"/"24" from Supabase (typed before this rule existed, or
   loaded from a real invoice) shows 2 decimals the instant the PO
   opens, without requiring him to click into every cell first. Only
   applied once, when a line is seeded into the form's `lines` state
   — never on every render, so it can't fight the user mid-keystroke. */
var PRICE_FIELD_KEYS = ["priceFSMin", "priceFSMax", "priceFS", "priceSL", "priceGF", "priceGrower", "priceFSReal", "priceSLReal", "priceGFReal", "priceGrowerReal"];
function formatLinePrices(rec) {
  var out = { ...rec };
  PRICE_FIELD_KEYS.forEach(function (k) {
    if (out[k] !== "" && out[k] != null) out[k] = fmt2(out[k]);
  });
  return out;
}
/* Column headers for the "one row per product" table (Packing-List
   style, per Arturo's reference image 2026-09-18) — thSub/thSubR are
   the 2nd header row (Pallets/Cases/Customer/GF/Grower etc.), under
   the merged group headers (thGroup/thGroupC) in the 1st row. */
/* whiteSpace:pre-line (2026-09-18, Arturo: "esto debe ser a 2 renglones")
   so a `\n` inside a column label (e.g. priceFSMin "Min Price\n
   (Customer)") renders as a real 2-line wrap instead of one long run-on
   line squeezing the narrow price columns — same pattern the outer
   list table's `th` already uses for its own 2-line column headers. */
const thSub = { padding: "3px 6px", fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, border: "1px solid var(--color-border)", whiteSpace: "pre-line", lineHeight: 1.2 };
const thSubR = { ...thSub, textAlign: "right" };
/* Group-level header cells (Product/Category/Ordered/Received/Firm
   Price/After Sale Price/Commission) — bordered box look matching
   Arturo's reference image, vertical dividers between groups. */
const thGroup = { textAlign: "left", padding: "4px 8px", fontSize: 10, color: "var(--color-text-secondary)", fontWeight: 700, border: "1px solid var(--color-border)", verticalAlign: "middle" };
const thGroupC = { ...thGroup, textAlign: "center" };

/* ---------- Storage helpers ---------- */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }
function getPOs() { return loadLS(STORAGE_KEY, []); }
function getClients() { return loadLS("gfi-clients", []); }
function getProducts() { return loadLS("gfi-products", []); }
function getInvoices() { return loadLS("gfi-invoices", []); }

/* ISO week derived from yyyy-mm-dd date string. Same impl pattern
   used in ShipmentRegistry / BillRegistry — kept local per the
   "intentionally not shared" rule. */
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  var target = new Date(d.valueOf());
  var day = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - day + 3);
  var firstThu = new Date(target.getFullYear(), 0, 4);
  var diff = target - firstThu;
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
}

/* Product label: "Blackberry 12/6oz OG GF Label". Pulled from
   product catalog for display in the dropdown + frozen onto the
   PO line at pick time. */
/* 2026-09-29 (Arturo: "en el modulo de ordenes, al seleccionar
   producto no puedo ver si es premium o no") — same gap already fixed
   in ProductRegistry.js's SKU/description and ShipperSettings.js's
   Authorized Products picker: qualityGrade was never surfaced in the
   product dropdown label here either, so a Premium product looked
   identical to its regular counterpart when picking a product for a
   PO line. */
function buildProductLabel(p) {
  if (!p) return "";
  var bits = [];
  if (p.product) bits.push(p.product);
  /* 2026-09-29 — real bug found alongside the qualityGrade fix above:
     this read `p.unitsCase`, a field that has never existed on a
     product record (the real one is `unitsPerCase`, per
     ProductRegistry.js/supabaseDB.js) — so this condition always fell
     through to size-only, silently hiding units/case from every PO
     line's product picker since this function was written. */
  if (p.unitsPerCase && p.size) bits.push(p.unitsPerCase + "/" + p.size);
  else if (p.size) bits.push(p.size);
  var cat = String(p.category || "").toLowerCase().indexOf("org") >= 0 ? "OG" : "CV";
  bits.push(cat);
  if (p.qualityGrade) bits.push(p.qualityGrade);
  if (p.labelInitials) bits.push(p.labelInitials + " Label");
  return bits.join(" ");
}

function fmtMoney(n) {
  var v = parseFloat(n);
  if (isNaN(v)) return "—";
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMoneyShort(n) {
  var v = parseFloat(n);
  if (isNaN(v) || v === 0) return "—";
  /* Thousand separators applied app-wide — match fmtMoney's
     formatting so $46,080.00 reads cleanly in tables/cards/print. */
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* Computed profits for a PO line. `useReal` switches the FS price
   from the captured estimate to the invoice-updated FS Real price
   when one exists, so the same helper drives both "Est." and
   "Real" columns. Cases received drops back to cases sent when
   the received count hasn't been captured yet. */
var REAL_KEYS = ["priceFSReal","priceSLReal","priceGFReal","priceGrowerReal","commissionPctReal","financeFeePaidReal","financeFeeChargedReal"];
function lineHasReal(line) {
  if (!line) return false;
  for (var i = 0; i < REAL_KEYS.length; i++) {
    var v = line[REAL_KEYS[i]];
    if (v !== "" && v != null && !isNaN(parseFloat(v))) return true;
  }
  return false;
}
function calcProfits(line, useReal) {
  var cases = parseInt(line.casesReceived, 10);
  if (!cases || cases <= 0) cases = parseInt(line.casesSent, 10) || 0;
  /* Pick real value when useReal AND that specific real field is set.
     Otherwise fall back to the estimated. Same pattern as Consignment. */
  var pick = function (realKey, estKey) {
    if (useReal && line[realKey] !== "" && line[realKey] != null && !isNaN(parseFloat(line[realKey]))) {
      return line[realKey];
    }
    return line[estKey];
  };
  var fsRaw = pick("priceFSReal", "priceFS");
  var slRaw = pick("priceSLReal", "priceSL");
  var gfRaw = pick("priceGFReal", "priceGF");
  var growerRaw = pick("priceGrowerReal", "priceGrower");
  var commRaw = pick("commissionPctReal", "commissionPct");
  var finPaidRaw = pick("financeFeePaidReal", "financeFeePaid");
  var finChargedRaw = pick("financeFeeChargedReal", "financeFeeCharged");
  var fs = parseFloat(fsRaw) || 0;
  var sl = parseFloat(slRaw) || 0;
  var gf = parseFloat(gfRaw) || 0;
  var grower = parseFloat(growerRaw) || 0;
  var commPct = parseFloat(commRaw);
  if (isNaN(commPct)) commPct = DEFAULT_COMMISSION;
  /* SL blank check on the PICKED value — if the effective SL is
     blank, spread is 0. Otherwise (fs - 0) would inflate profit. */
  var slBlank = (slRaw === undefined || slRaw === null || String(slRaw).trim() === "");
  var profitSL = slBlank ? 0 : (fs - sl) * cases;
  var profitDiff = (gf - grower) * cases;
  var profitComm = grower * (commPct / 100) * cases;
  var finPaid = parseFloat(finPaidRaw) || 0;
  var finCharged = parseFloat(finChargedRaw) || 0;
  var profitFinance = finCharged - finPaid;
  return { profitSL: profitSL, profitDiff: profitDiff, profitComm: profitComm, profitFinance: profitFinance, total: profitSL + profitDiff + profitComm + profitFinance };
}

/* Consignment profit math (2026-09-18, Phase 2) — deliberately kept
   as its OWN function, exact same formula as
   ConsignmentPricingRegistry.js's calcConsignment(), never merged
   into calcProfits() above: the two models diverge too much (a price
   RANGE + client commission here, vs a single captured price there)
   to share one function without conditionals fighting each other.
   Estimated math always uses the estimated inputs; Real math (when
   priceRealTR is present) is computed entirely separately, exactly
   like the original screen — never a blended "useReal" flag. */
function calcConsignment(line) {
  var cases = parseInt(line.cases, 10) || 0;
  var minTR = parseFloat(line.minTR) || 0;
  var maxTR = parseFloat(line.maxTR) || 0;
  var commTR = parseFloat(line.commTR) || 0;
  var differential = parseFloat(line.differential) || 0;
  var commGFI = parseFloat(line.commGFI) || 0;

  var hasEstTR = line.priceEstTR !== "" && line.priceEstTR != null && !isNaN(parseFloat(line.priceEstTR));
  var midTR = hasEstTR ? parseFloat(line.priceEstTR) : (minTR + maxTR) / 2;
  var midGF = midTR * (1 - commTR / 100);
  var midGrower = midGF - differential;
  var minGF = minTR * (1 - commTR / 100);
  var maxGF = maxTR * (1 - commTR / 100);
  var minGrower = minGF - differential;
  var maxGrower = maxGF - differential;

  var profitDiff = differential * cases;
  var profitComm = midGrower * (commGFI / 100) * cases;

  var hasReal = line.priceRealTR !== "" && line.priceRealTR != null && !isNaN(parseFloat(line.priceRealTR));
  var realGF = null, realGrower = null, realProfitDiff = 0, realProfitComm = 0, realTotal = null;
  if (hasReal) {
    var priceRealTR = parseFloat(line.priceRealTR);
    var realCommTR = (line.realCommTR !== "" && line.realCommTR != null && !isNaN(parseFloat(line.realCommTR))) ? parseFloat(line.realCommTR) : commTR;
    var realDifferential = (line.realDifferential !== "" && line.realDifferential != null && !isNaN(parseFloat(line.realDifferential))) ? parseFloat(line.realDifferential) : differential;
    var realCommGFI = (line.realCommGFI !== "" && line.realCommGFI != null && !isNaN(parseFloat(line.realCommGFI))) ? parseFloat(line.realCommGFI) : commGFI;
    realGF = priceRealTR * (1 - realCommTR / 100);
    realGrower = realGF - realDifferential;
    realProfitDiff = realDifferential * cases;
    realProfitComm = realGrower * (realCommGFI / 100) * cases;
    realTotal = realProfitDiff + realProfitComm;
  }

  return {
    midTR: midTR, midGF: midGF, midGrower: midGrower,
    minGF: minGF, maxGF: maxGF, minGrower: minGrower, maxGrower: maxGrower,
    profitDiff: profitDiff, profitComm: profitComm, total: profitDiff + profitComm,
    hasReal: hasReal, realGF: realGF, realGrower: realGrower,
    realProfitDiff: realProfitDiff, realProfitComm: realProfitComm, realTotal: realTotal,
  };
}

/* Term-aware wrappers (2026-09-18, Phase 2) — every place in the
   OUTER list (stats, sort, exports, table cells) that used to call
   calcProfits()/lineHasReal() directly now goes through these, so a
   Consignment row contributes its REAL numbers to totals/exports
   instead of silently reading as $0 (which is what calcProfits()
   would return for fields that don't exist on a Consignment record).
   Normalizes calcConsignment()'s different shape (profitDiff/
   profitComm only, no profitSL/profitFinance) into the same 5-key
   shape calcProfits() already returns. */
function profitFor(line, useReal) {
  if (line && line.term === "consignment") {
    var c = calcConsignment(line);
    var real = useReal && c.hasReal;
    return { profitSL: 0, profitDiff: real ? c.realProfitDiff : c.profitDiff, profitComm: real ? c.realProfitComm : c.profitComm, profitFinance: 0, total: real ? c.realTotal : c.total };
  }
  return calcProfits(line, useReal);
}
function lineHasRealAny(line) {
  if (line && line.term === "consignment") return calcConsignment(line).hasReal;
  return lineHasReal(line);
}
/* Customer-facing price (Firm/estimated) for revenue totals — priceFS
   for Fixed/Semi-fixed, Mid TR for Consignment (its range midpoint,
   same value calcConsignment() itself uses as "the" TR price). */
function customerPriceFor(r) {
  if (r && r.term === "consignment") return calcConsignment(r).midTR;
  return parseFloat(r.priceFS) || 0;
}
/* Real cases shipped for revenue/aggregate totals — Fixed/Semi-fixed
   prefer Received over Sent (what actually went out); Consignment
   only ever captures one `cases` field (no Sent/Received split). */
function casesFor(r) {
  if (r && r.term === "consignment") return parseInt(r.cases, 10) || 0;
  var cases = parseInt(r.casesReceived, 10);
  if (!cases || cases <= 0) cases = parseInt(r.casesSent, 10) || 0;
  return cases;
}
/* Same idea as casesFor, for the Pallets Sent column's sort (2026-09-18,
   Arturo: "necesito la tabla... pueda ordenar todas las columnas"). */
function palletsFor(r) {
  if (r && r.term === "consignment") return parseFloat(r.pallets) || 0;
  return parseFloat(r.palletsSent) || 0;
}

/* ---------- Inline UI primitives ---------- */
function Field({ label, required, error, sub, children, full, flexBasis }) {
  /* flexBasis (2026-09-18, Arturo: "el renglon PO header debe estar
     alineado horizaontalmnente, loading dalivey day esta por debajo
     de los demas campos") — the header row is now a `nowrap` flex
     row (see PO Header below), not a CSS grid. Grid's `auto-fit` was
     still letting fields WRAP onto a second/third line whenever the
     row didn't have room, which is exactly the "one field stranding
     below the rest" symptom Arturo kept seeing. `flexBasis` lets a
     field opt into a fixed width (e.g. the narrow Week field) while
     every other field defaults to `flex:"1 1 0"` + `minWidth:0` so
     it shrinks instead of wrapping — the whole row always stays on
     ONE line, literally "alineado horizontalmente", with the row's
     own `overflowX:auto` as the only fallback if it truly can't fit
     (rare, only on very narrow phones). */
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: flexBasis ? ("0 0 " + flexBasis) : "1 1 0", minWidth: flexBasis || 0, gridColumn: full ? "1 / -1" : "auto" }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 3, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
        {label}{required && <span style={{ color: "#DC2626", fontSize: 10 }}>*</span>}
      </label>
      {children}
      {sub && <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic", whiteSpace: "nowrap" }}>{sub}</span>}
      {error && <span style={{ fontSize: 10, color: "#DC2626", fontWeight: 600 }}>{error}</span>}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid var(--color-border)", paddingBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied here since
   this single StatusBadge renders all 3 status concepts on this
   screen (PO Status, Arrival Status, Sale Status) in both the list
   table and the PO Header of the edit form. */
var STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";
function StatusBadge({ status, t }) {
  var c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  var label = t["status" + (status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending")] || status;
  return <span style={{ padding: "3px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: c.bg, color: c.text, border: "1px solid " + c.border, boxShadow: STATUS_BEVEL, whiteSpace: "nowrap", display: "inline-block" }}>{label}</span>;
}
function Toast({ msg, onDismiss }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    if (!msg) return;
    setFading(false);
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(onDismiss, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
    /* onDismiss deliberately excluded — inline arrow from parent
       creates a new identity per render, resetting the timer. The
       setToast setter captured inside onDismiss is stable, so the
       closure remains safe. */
  }, [msg]);
  if (!msg) return null;
  return createPortal(<div style={{ position: "fixed", bottom: 24, right: 24, padding: "10px 18px", background: "var(--color-accent)", color: "var(--color-accent-text)", borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 999999, animation: fading ? "fadeOutCorner 0.3s ease forwards" : undefined }}>{msg}</div>, document.body);
}

/* Fields that belong to the PO as a whole (shared by every product
   line under it) vs. fields that belong to one product line. Split
   introduced 2026-09-18 (Arturo: "debo poder agregar lineas de
   producto es decir, un po puede tener mas de un producto... quantity
   and pricing deben convivir en el mismo renglon del producto para
   agregar mas producto debajo de el") — each product line is STILL
   its own independent fixed_pos record on save (Option A, confirmed
   with Arturo: never restructure into a real parent/child so existing
   invoice links never break), just edited together in one sitting. */
/* saleStatus moved here 2026-09-18 (Arturo: "sale status sacalo de
   ahi, colocalo dentro del PO header") — one status for the whole PO,
   not per product line; spreading header over each line on save
   (`{...ln, ...header}`) means every product under this PO gets the
   same saleStatus written to its own record. */
/* puNumber moved here too, same day (Arturo: "PU# tampoco va dentro de
   product... a un PO no se le asignan varios PU, 1 po = 1 pu") — one
   PU# for the whole PO, mirrored onto every product line's own record
   on save via the same header spread. */
var HEADER_KEYS = ["date", "week", "deliveryDate", "clientId", "clientName", "poNumber", "puNumber", "loggerNumber", "term", "seasonId", "saleStatus", "settlementStatus"];
function extractHeader(rec) {
  var h = {};
  HEADER_KEYS.forEach(function (k) { h[k] = rec[k]; });
  return h;
}

/* One product line — Product/Category, Ordered vs. Actual quantities,
   Pricing (term/client-conditional), After-Sale Real prices, and its
   own Sale Status/Notes (each saved record genuinely has its own —
   never shared across lines, e.g. one product can ship while another
   in the same PO hasn't). */
/* Compact table cell input — no label, just the control, since column
   headers already say what each cell is (same "one row, many columns"
   pattern as this app's Packing List, per Arturo 2026-09-18: "recuerda
   como se crea el packing list, en un renglon existen varias
   columnas, haz lo mismo aca"). */
var tdInp = { padding: "3px 4px" };
function ProductLineTableRow({ ln, idx, isSemifixed, isSweetLeaf, locked, errors, onChange, onRemove, canRemove, t, productOpts }) {
  var set = function (k, v) { onChange(idx, k, v); };
  var handleProductChange = function (productId, products) {
    var prod = products.find(function (p) { return String(p.id) === String(productId); });
    if (!prod) { set("productId", productId); return; }
    var cat = String(prod.category || "").toLowerCase().indexOf("org") >= 0 ? "OG" : "CV";
    onChange(idx, "productId", productId);
    onChange(idx, "productBase", prod.product || "");
    onChange(idx, "productName", prod.product || "");
    onChange(idx, "productDescription", buildProductLabel(prod));
    onChange(idx, "category", cat);
  };
  var midpoint = isSemifixed ? (function () {
    var min = parseFloat(ln.priceFSMin), max = parseFloat(ln.priceFSMax);
    if (isNaN(min) || isNaN(max)) return null;
    return (min + max) / 2;
  })() : null;
  var err = errors || {};
  var numCell = function (key, extraStyle) {
    return (
      <td style={tdInp}>
        <input type="number" min="0" step="0.01" value={ln[key] || ""} onChange={function (e) { set(key, e.target.value); }} onBlur={function (e) { priceBlur(e, key, set); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: capBg(ln[key]), ...(extraStyle || {}), ...(err[key] ? errS : {}) }} />
      </td>
    );
  };
  return (
    <tr>
      <td style={{ ...tdInp, minWidth: 170 }}><ProductPicker value={ln.productId} onChange={handleProductChange} placeholder={t.selectProduct} options={productOpts} disabled={locked} style={{ background: capBg(ln.productId) }} /></td>
      <td style={tdInp}><input value={ln.category || ""} readOnly style={{ ...roInp, color: "var(--color-text-muted)", textAlign: "center", fontWeight: 700, width: 46 }} /></td>
      <td style={tdInp}><input type="number" min="0" value={ln.palletsSent || ""} onChange={function (e) { set("palletsSent", e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: capBg(ln.palletsSent), width: 56 }} /></td>
      <td style={tdInp}><input type="number" min="0" value={ln.casesSent || ""} onChange={function (e) { set("casesSent", e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: capBg(ln.casesSent), width: 56, ...(err.casesSent ? errS : {}) }} /></td>
      <td style={tdInp}><input type="number" min="0" value={ln.palletsReceived || ""} onChange={function (e) { set("palletsReceived", e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: capBg(ln.palletsReceived), width: 56 }} /></td>
      <td style={tdInp}><input type="number" min="0" value={ln.casesReceived || ""} onChange={function (e) { set("casesReceived", e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: capBg(ln.casesReceived), width: 56 }} /></td>
      {isSemifixed ? (
        <>
          <td style={tdInp}><input type="number" min="0" step="0.01" value={ln.priceFSMin || ""} onChange={function (e) { set("priceFSMin", e.target.value); }} onBlur={function (e) { priceBlur(e, "priceFSMin", set); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: capBg(ln.priceFSMin), width: 64, ...(err.priceFSMin ? errS : {}) }} /></td>
          <td style={tdInp}><input type="number" min="0" step="0.01" value={ln.priceFSMax || ""} onChange={function (e) { set("priceFSMax", e.target.value); }} onBlur={function (e) { priceBlur(e, "priceFSMax", set); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: capBg(ln.priceFSMax), width: 64 }} /></td>
          <td style={tdInp}><input value={midpoint != null ? midpoint.toFixed(2) : "—"} readOnly className="gfi-num-input" style={{ ...roInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", width: 64 }} /></td>
        </>
      ) : numCell("priceFS", { fontWeight: 600, width: 64 })}
      {isSweetLeaf && numCell("priceSL", { width: 64 })}
      {numCell("priceGF", { width: 64 })}
      {numCell("priceGrower", { width: 64 })}
      {numCell("priceFSReal", { fontWeight: 700, width: 64, background: realBg(ln.priceFSReal) })}
      {isSweetLeaf && numCell("priceSLReal", { fontWeight: 700, width: 64, background: realBg(ln.priceSLReal) })}
      {numCell("priceGFReal", { fontWeight: 700, width: 64, background: realBg(ln.priceGFReal) })}
      {numCell("priceGrowerReal", { fontWeight: 700, width: 64, background: realBg(ln.priceGrowerReal) })}
      <td style={tdInp}><input type="number" min="0" step="0.01" value={ln.commissionPct === "" || ln.commissionPct == null ? "" : ln.commissionPct} onChange={function (e) { set("commissionPct", e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: capBg(ln.commissionPct), width: 56 }} /></td>
      <td style={tdInp}>
        {canRemove && !locked && (
          <button type="button" onClick={function () { onRemove(idx); }} title={t.removeProduct} style={{ padding: "2px 7px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>✕</button>
        )}
      </td>
    </tr>
  );
}
/* Consignment product line (2026-09-18, Phase 2) — deliberately its
   OWN row component, not another branch inside ProductLineTableRow:
   the field set (a price RANGE + client commission + differential,
   no Sent/Received split) is different enough that threading it
   through the Fixed row would mean more conditionals than shared
   code. Same "one row, many columns" pattern, same capBg/realBg/
   gfi-num-input/priceBlur conventions as the Fixed row. */
function ConsignmentLineTableRow({ ln, idx, locked, errors, onChange, onRemove, canRemove, t, productOpts }) {
  var set = function (k, v) { onChange(idx, k, v); };
  var handleProductChange = function (productId, products) {
    var prod = products.find(function (p) { return String(p.id) === String(productId); });
    if (!prod) { set("productId", productId); return; }
    var cat = String(prod.category || "").toLowerCase().indexOf("org") >= 0 ? "OG" : "CV";
    onChange(idx, "productId", productId);
    onChange(idx, "productBase", prod.product || "");
    onChange(idx, "productName", prod.product || "");
    onChange(idx, "productDescription", buildProductLabel(prod));
    onChange(idx, "category", cat);
  };
  var err = errors || {};
  var c = calcConsignment(ln);
  var priceCell = function (key, extraStyle) {
    return (
      <td style={tdInp}>
        <input type="number" min="0" step="0.01" value={ln[key] || ""} onChange={function (e) { set(key, e.target.value); }} onBlur={function (e) { priceBlur(e, key, set); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: capBg(ln[key]), ...(extraStyle || {}), ...(err[key] ? errS : {}) }} />
      </td>
    );
  };
  /* Percent fields — same capture-highlight convention, but never
     forced to 2 decimals (a commission % isn't money). */
  var pctCell = function (key, extraStyle) {
    return (
      <td style={tdInp}>
        <input type="number" min="0" step="0.01" value={ln[key] === "" || ln[key] == null ? "" : ln[key]} onChange={function (e) { set(key, e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: capBg(ln[key]), ...(extraStyle || {}) }} />
      </td>
    );
  };
  var computedCell = function (val, extraStyle) {
    return (
      <td style={tdInp}>
        <input value={val != null && !isNaN(val) ? val.toFixed(2) : "—"} readOnly className="gfi-num-input" style={{ ...roInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, width: 64, ...(extraStyle || {}) }} />
      </td>
    );
  };
  return (
    <tr>
      <td style={{ ...tdInp, minWidth: 170 }}><ProductPicker value={ln.productId} onChange={handleProductChange} placeholder={t.selectProduct} options={productOpts} disabled={locked} style={{ background: capBg(ln.productId) }} /></td>
      <td style={tdInp}><input value={ln.category || ""} readOnly style={{ ...roInp, color: "var(--color-text-muted)", textAlign: "center", fontWeight: 700, width: 46 }} /></td>
      <td style={tdInp}><input type="number" min="0" value={ln.pallets || ""} onChange={function (e) { set("pallets", e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: capBg(ln.pallets), width: 56 }} /></td>
      <td style={tdInp}><input type="number" min="0" value={ln.cases || ""} onChange={function (e) { set("cases", e.target.value); }} disabled={locked} className="gfi-num-input" style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: capBg(ln.cases), width: 56, ...(err.cases ? errS : {}) }} /></td>
      {priceCell("minTR", { fontWeight: 600, width: 64, ...(err.minTR ? errS : {}) })}
      {priceCell("maxTR", { fontWeight: 600, width: 64 })}
      {pctCell("commTR", { width: 52 })}
      {priceCell("differential", { width: 64 })}
      {pctCell("commGFI", { width: 52 })}
      {priceCell("advance", { width: 64 })}
      {computedCell(c.midTR, { color: "var(--color-accent)" })}
      {computedCell(c.midGF)}
      {computedCell(c.midGrower)}
      {priceCell("priceRealTR", { fontWeight: 700, width: 64, background: realBg(ln.priceRealTR) })}
      {pctCell("realCommTR", { width: 52, background: realBg(ln.realCommTR) })}
      {priceCell("realDifferential", { width: 64, background: realBg(ln.realDifferential) })}
      {pctCell("realCommGFI", { width: 52, background: realBg(ln.realCommGFI) })}
      {computedCell(c.realGF, { color: c.hasReal ? "#047857" : "var(--color-text-muted)" })}
      {computedCell(c.realGrower, { color: c.hasReal ? "#047857" : "var(--color-text-muted)" })}
      <td style={tdInp}>
        {canRemove && !locked && (
          <button type="button" onClick={function () { onRemove(idx); }} title={t.removeProduct} style={{ padding: "2px 7px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>✕</button>
        )}
      </td>
    </tr>
  );
}
/* Thin wrapper so ProductLineTableRow can pass `products` through to
   its handleProductChange without every SearchSelect caller needing
   the full product list threaded as a prop name collision risk. */
function ProductPicker({ value, onChange, placeholder, options, disabled, style }) {
  var products = useMemo(function () { return getProducts(); }, []);
  /* 2026-10-08, Arturo: "al igual que hiciste en los embarques al
     abrir el dropdown list del grower code... necesito hagas lo mismo
     con el dropdown de select product en los weekly po's... el campo
     no se amplia, solo al abrir el drop la ventana debe ser mas
     ancha" — same SearchSelect props as ShipmentRegistry.js's Grower
     Code: the trigger cell keeps its normal column width, only the
     open panel widens so a full product description (e.g. "Blackberry
     12/6oz OG Premium GF Label") reads on one line. */
  return <SearchSelect value={value} onChange={function (v) { onChange(v, products); }} placeholder={placeholder} options={options} disabled={disabled} style={style} nowrap panelMinWidth={360} />;
}

function ProfitCell({ title, amount, desc, emphasis }) {
  var positive = amount > 0; var negative = amount < 0;
  var color = emphasis ? "var(--color-accent)" : (positive ? "#047857" : negative ? "#B91C1C" : "var(--color-text-secondary)");
  return (
    <div style={{ padding: "10px 12px", borderRadius: 6, border: "1px solid " + (emphasis ? "var(--color-accent)" : "var(--color-border)"), background: emphasis ? "rgba(42,122,111,0.05)" : "var(--color-bg)" }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>
      <div style={{ fontSize: emphasis ? 18 : 14, fontWeight: emphasis ? 800 : 700, fontFamily: "'JetBrains Mono', monospace", color: color, marginTop: 4 }}>{fmtMoney(amount)}</div>
      <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2, fontStyle: "italic" }}>{desc}</div>
    </div>
  );
}

/* ======================== FORM MODAL ======================== */
function PoLineModal({ line, allRows, t, lang, onSaveLines, onDeleteLines, onClose }) {
  var isNew = !line || !line.id;
  var [mode, setMode] = useState(isNew ? "edit" : "view");
  var [header, setHeader] = useState(function () { return line ? extractHeader({ ...EMPTY, ...line }) : extractHeader(EMPTY); });
  var [lines, setLines] = useState(function () {
    if (!line) return [{ ...EMPTY }];
    var siblings = (allRows || []).filter(function (r) { return r.poNumber === line.poNumber && r.clientId === line.clientId; });
    if (!siblings.length) siblings = [line];
    siblings = siblings.slice().sort(function (a, b) { return (a.createdAt || "") < (b.createdAt || "") ? -1 : 1; });
    return siblings.map(function (r) { return formatLinePrices({ ...EMPTY, ...r }); });
  });
  var [headerErrors, setHeaderErrors] = useState({});
  var [lineErrors, setLineErrors] = useState([]);
  var [dirty, setDirty] = useState(false);
  var [showExit, setShowExit] = useState(false);
  var [pendingDeletes, setPendingDeletes] = useState([]);
  var origRef = useMemo(function () { return JSON.stringify({ header: header, lines: lines }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var clients = useMemo(function () { return getClients(); }, []);
  var products = useMemo(function () { return getProducts(); }, []);
  var seasons = useMemo(function () { return loadSeasons(); }, []);

  var setH = function (k, v) { setHeader(function (prev) { return { ...prev, [k]: v }; }); setDirty(true); setHeaderErrors(function (p) { return { ...p, [k]: false }; }); };
  var setLineField = function (idx, k, v) {
    setLines(function (prev) { return prev.map(function (ln, i) { return i === idx ? { ...ln, [k]: v } : ln; }); });
    setDirty(true);
    setLineErrors(function (prev) {
      var next = prev.slice();
      if (next[idx]) next[idx] = { ...next[idx], [k]: false };
      return next;
    });
  };
  var addLine = function () { setLines(function (prev) { return prev.concat([{ ...EMPTY }]); }); setDirty(true); };
  var removeLine = function (idx) {
    setLines(function (prev) {
      var target = prev[idx];
      if (target && target.id) setPendingDeletes(function (p) { return p.concat([target.id]); });
      return prev.filter(function (_, i) { return i !== idx; });
    });
    setDirty(true);
  };

  /* Week is derived from the Loading/Delivery Date (when the
     warehouse receives), NOT from the MX departure date. Season
     still tracks the departure date. Editing departure leaves week
     alone. */
  var handleDateChange = function (v) {
    setHeader(function (prev) {
      var next = { ...prev, date: v };
      var sid = resolveSeasonId(seasons, v, prev.seasonId);
      if (sid) next.seasonId = sid;
      return next;
    });
    setDirty(true);
    setHeaderErrors(function (p) { return { ...p, date: false }; });
  };
  /* Delivery Date → MX Departure (Shipping Date) auto-fill. Truck
     leaves MX 2 days before the warehouse receives. Always recomputes
     when delivery changes — user can override after by editing the
     Date field directly. Week is computed from the delivery date
     itself; season tracks the derived departure date. */
  var handleDeliveryDateChange = function (v) {
    setHeader(function (prev) {
      var next = { ...prev, deliveryDate: v, week: v ? getISOWeek(v) : "" };
      if (v) {
        var d = new Date(v + "T00:00:00");
        if (!isNaN(d.getTime())) {
          d.setDate(d.getDate() - 2);
          var yyyy = d.getFullYear();
          var mm = String(d.getMonth() + 1).padStart(2, "0");
          var dd = String(d.getDate()).padStart(2, "0");
          var shipISO = yyyy + "-" + mm + "-" + dd;
          next.date = shipISO;
          var sid = resolveSeasonId(seasons, shipISO, prev.seasonId);
          if (sid) next.seasonId = sid;
        }
      }
      return next;
    });
    setDirty(true);
    setHeaderErrors(function (p) { return { ...p, date: false }; });
  };
  var handleClientChange = function (clientId) {
    var cl = clients.find(function (c) { return String(c.id) === String(clientId); });
    setHeader(function (prev) { return { ...prev, clientId: clientId, clientName: cl ? (cl.shortName || cl.fullName || cl.name || "") : "" }; });
    setDirty(true);
  };

  var isSemifixed = header.term === "semifixed";
  var isConsignment = header.term === "consignment";
  var isSweetLeaf = isSweetLeafClient(header.clientName);

  /* PO Status / Arrival Status for the header display — aggregated
     across every product line under this PO (Option A keeps each
     line as its own record, so each can in theory link to a
     different invoice). Each line already carries the live-computed
     values (seeded from `allRows`, which is the enriched list — see
     FixedPricingRegistry's `enriched` useMemo). PO Status: invoiced
     the moment ANY line has been used on an invoice. Arrival Status:
     same mixing rule as pallets/Bill/Shipment (deriveDeliveryStatus)
     — a PO whose lines disagree shows the same "partial" read those
     other modules already show for a mixed lot.
     headerPoStatus reads `ln.saleStatus` directly (2026-09-18, real
     bug caught live: PO 2228525 kept showing "Invoiced" here even
     after its linked invoice was deleted, because the OLD check only
     tested "is linkedInvoiceId truthy" — true for a stale/orphaned id
     pointing at a deleted invoice — instead of trusting the same
     invoice-EXISTENCE check `enriched`'s computeLiveStatuses() already
     did upstream, which the outer list's PO Status column already
     used correctly and showed "Pending Invoice" for the exact same
     row, at the same time, inconsistently). `ln.saleStatus` is
     exactly that already-correct value — no need to re-derive it. */
  var headerPoStatus = lines.some(function (ln) { return ln.saleStatus === "invoiced"; }) ? "invoiced" : "pending";
  var headerArrivalStatus = (function () {
    var vals = lines.map(function (ln) { return ln.arrivalStatus; }).filter(Boolean);
    if (vals.length === 0) return "";
    return deriveDeliveryStatus(vals.map(function (v) { return { saleStatus: v }; })) || vals[0];
  })();
  var settlementOpts = SETTLEMENT_STATUSES.map(function (s) { return { value: s, label: t["status" + s.charAt(0).toUpperCase() + s.slice(1)] || s }; });

  var validate = function () {
    var hErrs = {};
    if (!header.date) hErrs.date = t.noDate;
    if (!String(header.poNumber || "").trim()) hErrs.poNumber = t.noPO;
    var lErrs = lines.map(function (ln) {
      var e = {};
      if (!ln.productId) e.productId = t.noProduct;
      if (isConsignment) {
        if (!String(ln.minTR || "").trim() || !String(ln.maxTR || "").trim()) e.minTR = t.noPriceFS;
        if (!parseInt(ln.cases, 10)) e.cases = t.noCases;
      } else {
        if (isSemifixed) {
          if (!String(ln.priceFSMin || "").trim() || !String(ln.priceFSMax || "").trim()) e.priceFSMin = t.noPriceFS;
        } else if (!String(ln.priceFS || "").trim()) {
          e.priceFS = t.noPriceFS;
        }
        if (!parseInt(ln.casesSent, 10)) e.casesSent = t.noCases;
      }
      return e;
    });
    setHeaderErrors(hErrs);
    setLineErrors(lErrs);
    var hasLineErrors = lErrs.some(function (e) { return Object.keys(e).length > 0; });
    return Object.keys(hErrs).length === 0 && !hasLineErrors;
  };
  var doSave = function (keepOpen) {
    if (!validate()) return;
    var nowIso = new Date().toISOString();
    /* Semi-fixed: the single priceFS used by calcProfits() and every
       table column is always the midpoint of the captured range —
       computed here on save so nothing downstream needs to know a
       "term" concept exists at all. */
    var records = lines.map(function (ln, i) {
      var midpoint = null;
      if (isSemifixed) {
        var min = parseFloat(ln.priceFSMin), max = parseFloat(ln.priceFSMax);
        if (!isNaN(min) && !isNaN(max)) midpoint = (min + max) / 2;
      }
      var rec = {
        ...ln,
        ...header,
        priceFS: isSemifixed && midpoint != null ? String(midpoint) : ln.priceFS,
        id: ln.id || ("PO-" + Date.now() + "-" + Math.floor(Math.random() * 1000) + "-" + i),
        createdAt: ln.createdAt || nowIso,
        updatedAt: nowIso,
      };
      /* arrivalStatus is computed live on every render from the linked
         invoice (see computeLiveStatuses) and must never be persisted —
         `ln` here was seeded from the `enriched` display row, which
         patches arrivalStatus onto it for rendering, so without this it
         silently leaks into Supabase's `extra` jsonb on every Save
         (found 2026-09-18 on PO 2228528, stuck at "pendingPickUp" from a
         since-deleted invoice). */
      delete rec.arrivalStatus;
      return rec;
    });
    onSaveLines(records, pendingDeletes);
    setPendingDeletes([]);
    setDirty(false);
    if (keepOpen) {
      /* Save & New: starts a completely fresh PO (new PO#) — carries
         date/client/term forward since those are usually the same for
         the next PO of the day, but always starts with exactly one
         blank product line. Adding more products to THIS PO is what
         the "+ Add Product" button below is for. */
      setHeader({ ...extractHeader(EMPTY), date: header.date, week: header.week, deliveryDate: header.deliveryDate, clientId: header.clientId, clientName: header.clientName, term: header.term, seasonId: header.seasonId });
      setLines([{ ...EMPTY }]);
      setHeaderErrors({});
      setLineErrors([]);
      setMode("edit");
    } else {
      setLines(records);
      setMode("view");
    }
  };
  var tryClose = function () {
    if (mode === "edit" && dirty && JSON.stringify({ header: header, lines: lines }) !== origRef) { setShowExit(true); return; }
    onClose();
  };
  var doDelete = function () {
    if (!window.confirm(t.confirmDel)) return;
    var ids = lines.map(function (ln) { return ln.id; }).filter(Boolean).concat(pendingDeletes);
    onDeleteLines(ids);
  };

  var locked = mode === "view";
  var clientOpts = clients.filter(function (c) { return c.isActive !== false; }).map(function (c) { return { value: String(c.id), label: c.shortName || c.fullName || c.name || "" }; });
  var productOpts = products.map(function (p) { return { value: String(p.id), label: buildProductLabel(p) }; });
  /* Same calcProfits() math per line, just summed across every product
     under this PO — one aggregate Profit Summary at the bottom, same
     5-metric breakdown as before the multi-product rewrite (Arturo,
     2026-09-18: "la seccion profit debe ser una sola en la parte
     inferior como estaba antes... los calculos son los mismos no
     cambiaron"). Real aggregate only shown when at least one line has
     real numbers captured.
     `term: header.term` forced on every line here (2026-09-18, Phase
     2 bug caught live testing) — each `lines[i]` only actually gets
     the header's term merged in at Save time (`doSave`'s `{...ln,
     ...header}`); while still editing, a brand-new Consignment line's
     own `ln.term` is still the stale EMPTY default ("fixed"), which
     made profitFor() pick calcProfits() (all $0, since none of the
     Fixed fields are set) instead of calcConsignment() for the LIVE
     preview — the per-row Mid TR/GF/Grower cells were already correct
     (calcConsignment() doesn't look at term at all), only this
     aggregate was wrong. */
  var aggProfits = lines.reduce(function (sum, ln) {
    var p = profitFor({ ...ln, term: header.term }, false);
    return { profitSL: sum.profitSL + p.profitSL, profitDiff: sum.profitDiff + p.profitDiff, profitComm: sum.profitComm + p.profitComm, profitFinance: sum.profitFinance + p.profitFinance, total: sum.total + p.total };
  }, { profitSL: 0, profitDiff: 0, profitComm: 0, profitFinance: 0, total: 0 });
  var anyReal = lines.some(function (ln) { return lineHasRealAny({ ...ln, term: header.term }); });
  var aggRealProfits = anyReal ? lines.reduce(function (sum, ln) {
    var lnT = { ...ln, term: header.term };
    var p = profitFor(lnT, lineHasRealAny(lnT));
    return { profitSL: sum.profitSL + p.profitSL, profitDiff: sum.profitDiff + p.profitDiff, profitComm: sum.profitComm + p.profitComm, profitFinance: sum.profitFinance + p.profitFinance, total: sum.total + p.total };
  }, { profitSL: 0, profitDiff: 0, profitComm: 0, profitFinance: 0, total: 0 }) : null;
  var anyLinked = lines.some(function (ln) { return ln.linkedInvoiceId; });

  return (
    <DraggableModal title={(isNew ? t.add : (locked ? t.view : t.edit)) + (header.poNumber ? "  ·  " + header.poNumber : "")} onClose={tryClose} initialWidth={Math.min(1100, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(820, window.innerHeight - 60)}>
      {showExit && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
          <div style={{ background: "var(--color-bg-card-solid)", borderRadius: 8, padding: 20, maxWidth: 380, border: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 13, color: "var(--color-text)", marginBottom: 16 }}>{t.confirmExit}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={function () { setShowExit(false); }} style={{ padding: "6px 14px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.noExit}</button>
              <button onClick={function () { setShowExit(false); onClose(); }} style={{ padding: "6px 14px", borderRadius: 5, border: "none", background: "#DC2626", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesExit}</button>
            </div>
          </div>
        </div>
      )}

      {/* Top action strip */}
      <div style={{ padding: "10px 18px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {header.week && <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600, background: "var(--color-bg-card)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)" }}>{t.weekShort + header.week}</span>}
          {anyLinked && <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.linkedInvoice}: <strong style={{ color: "var(--color-accent)" }}>✓</strong></span>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {!isNew && locked && <button onClick={doDelete} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.del}</button>}
          {locked && <button onClick={function () { setMode("edit"); }} style={{ padding: "6px 14px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.editBtn}</button>}
          {!locked && <button onClick={function () { doSave(true); }} style={{ padding: "6px 14px", borderRadius: 5, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.saveAndNew}</button>}
          {!locked && <button onClick={function () { doSave(false); }} style={{ padding: "6px 16px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.save}</button>}
          <button onClick={tryClose} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.closeBtn}</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px" }}>
        <Section title={t.secHeader}>
          {/* PO Header fields ALWAYS stay on one row (2026-09-18, Arturo:
             "el renglon PO header debe estar alineado horizaontalmnente,
             loading dalivey day esta por debajo de los demas campos") —
             the earlier CSS Grid `auto-fit` still WRAPPED fields onto a
             second/third line once the row ran out of room, which is
             exactly the "field stranding below the rest" symptom.
             Switched to a plain `nowrap` flex row: every field defaults
             to `flex:"1 1 0"` + `minWidth:0` (via Field's flexBasis
             prop) so it SHRINKS to fit instead of wrapping — the row
             is always literally one horizontal line. `overflowX:auto`
             is the only fallback if it truly can't fit (very narrow
             phone), never a wrap. */}
          <div style={{ display: "flex", flexWrap: "nowrap", gap: 10, overflowX: "auto", paddingBottom: 2 }}>
            <Field label={t.deliveryDate} required error={headerErrors.deliveryDate}>
              <input type="date" value={header.deliveryDate || ""} onChange={function (e) { handleDeliveryDateChange(e.target.value); }} disabled={locked} style={{ ...inp, background: capBg(header.deliveryDate), ...(headerErrors.deliveryDate ? errS : {}) }} />
            </Field>
            <Field label={t.date} sub={t.departureHint}>
              <input type="date" value={header.date || ""} onChange={function (e) { handleDateChange(e.target.value); }} disabled={locked} style={{ ...inp, background: capBg(header.date), ...(headerErrors.date ? errS : {}) }} />
            </Field>
            <Field label={t.weekShort} flexBasis="64px">
              <input value={header.week ? "W" + header.week : ""} readOnly style={{ ...roInp, color: "var(--color-text-muted)", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} />
            </Field>
            <Field label={t.client}>
              <SearchSelect value={header.clientId} onChange={handleClientChange} placeholder={t.selectClient} options={clientOpts} disabled={locked} style={{ background: capBg(header.clientId) }} />
            </Field>
            <Field label={t.poNumber} required error={headerErrors.poNumber}>
              <input value={header.poNumber || ""} onChange={function (e) { setH("poNumber", e.target.value); }} disabled={locked} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: capBg(header.poNumber), ...(headerErrors.poNumber ? errS : {}) }} />
            </Field>
            <Field label={t.term}>
              <SearchSelect value={header.term || "fixed"} onChange={function (v) { setH("term", v); }} options={TERM_OPTIONS.map(function (v) { return { value: v, label: t["term" + v.charAt(0).toUpperCase() + v.slice(1)] }; })} disabled={locked} style={{ background: capBg(header.term) }} />
            </Field>
            <Field label={t.puNumber}>
              <input value={header.puNumber || ""} readOnly style={{ ...roInp, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
            {/* PO Status — read-only, computed live (2026-09-18,
               Arturo: "no es editable y cambia en vivo... al asignar
               PO cambia a Invoiced"). Same DB key (saleStatus) as
               before so nothing linking to it breaks, just no longer
               a picker. */}
            <Field label={t.poStatus}>
              <div style={{ display: "flex", alignItems: "center", minHeight: 32 }}>
                <StatusBadge status={headerPoStatus} t={t} />
              </div>
            </Field>
            {/* Arrival Status — read-only, computed live, mirrors the
               linked invoice's own delivery status (pending pick up/
               delivery, good delivery, protection, PAS...). Never
               persisted — see computeLiveStatuses. */}
            <Field label={t.arrivalStatus}>
              <div style={{ display: "flex", alignItems: "center", minHeight: 32 }}>
                {headerArrivalStatus ? <StatusBadge status={headerArrivalStatus} t={t} /> : <span style={{ color: "var(--color-text-muted)", fontSize: 12 }}>—</span>}
              </div>
            </Field>
            {/* Sale Status — the only MANUAL one of the 3 (2026-09-18,
               Arturo: financial settlement, independent of PO Status
               and Arrival Status). */}
            <Field label={t.saleStatus}>
              <SearchSelect value={header.settlementStatus || ""} onChange={function (v) { setH("settlementStatus", v); }} placeholder={t.selectSettlement} options={settlementOpts} disabled={locked} style={{ background: capBg(header.settlementStatus) }} />
            </Field>
          </div>
        </Section>

        <Section title={t.secProduct}>
          {/* One row per product, many columns — same pattern as this
              app's Packing List (Arturo, 2026-09-18: "recuerda como se
              crea el packing list, en un renglon existen varias
              columnas, haz lo mismo aca"). Previous card-per-product
              layout took up almost the whole screen for one product;
              this keeps every product to exactly one compact row. */}
          <div style={{ overflowX: "auto", marginBottom: 8 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
              {isConsignment ? (
                <>
                  {/* Consignment (Phase 2, 2026-09-18) — its own column
                     set, a price RANGE + client commission instead of a
                     single captured price. Same 2-row grouped header
                     pattern as the Fixed table above. */}
                  <thead>
                    <tr>
                      <th style={thGroup} rowSpan={2}>{t.product}</th>
                      <th style={thGroup} rowSpan={2}>{t.category}</th>
                      <th style={thGroupC} colSpan={2}>{t.colGroupOrdered}</th>
                      <th style={thGroupC} colSpan={6}>{t.trPriceGroup}</th>
                      <th style={thGroupC} colSpan={3}>{t.firmPriceGroup}</th>
                      <th style={thGroupC} colSpan={6}>{t.trRealGroup}</th>
                      <th style={thGroup} rowSpan={2}></th>
                    </tr>
                    <tr>
                      <th style={thSub}>{t.palletsSent}</th>
                      <th style={thSub}>{t.casesSent}</th>
                      <th style={thSubR}>{t.minTR}</th>
                      <th style={thSubR}>{t.maxTR}</th>
                      <th style={thSubR}>{t.commTR}</th>
                      <th style={thSubR}>{t.differential}</th>
                      <th style={thSubR}>{t.commGFI}</th>
                      <th style={thSubR}>{t.advance}</th>
                      <th style={thSubR}>{t.midTRCol}</th>
                      <th style={thSubR}>{t.midGFCol}</th>
                      <th style={thSubR}>{t.midGrowerCol}</th>
                      <th style={thSubR}>{t.realTR}</th>
                      <th style={thSubR}>{t.realCommTR}</th>
                      <th style={thSubR}>{t.realDifferential}</th>
                      <th style={thSubR}>{t.realCommGFI}</th>
                      <th style={thSubR}>{t.realGFCol}</th>
                      <th style={thSubR}>{t.realGrowerCol}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(function (ln, idx) {
                      return (
                        <ConsignmentLineTableRow
                          key={ln.id || "new-" + idx}
                          ln={ln} idx={idx} locked={locked}
                          errors={lineErrors[idx]}
                          onChange={setLineField} onRemove={removeLine} canRemove={lines.length > 1}
                          t={t} productOpts={productOpts}
                        />
                      );
                    })}
                  </tbody>
                </>
              ) : (
                <>
                  {/* Group header row — Product/Category/Commission span
                      both header rows; Ordered/Received/Firm Price/After
                      Sale Price each get one merged header over their
                      sub-columns, matching Arturo's reference image
                      exactly (2026-09-18: "ve la imagen, asi lo quiero"). */}
                  <thead>
                    <tr>
                      <th style={thGroup} rowSpan={2}>{t.product}</th>
                      <th style={thGroup} rowSpan={2}>{t.category}</th>
                      <th style={thGroupC} colSpan={2}>{t.colGroupOrdered}</th>
                      <th style={thGroupC} colSpan={2}>{t.colGroupReceived}</th>
                      <th style={thGroupC} colSpan={(isSemifixed ? 3 : 1) + (isSweetLeaf ? 1 : 0) + 2}>{t.firmPriceGroup}</th>
                      <th style={thGroupC} colSpan={1 + (isSweetLeaf ? 1 : 0) + 2}>{t.afterSalePriceGroup}</th>
                      <th style={thGroup} rowSpan={2} title={t.commHint}>{t.commissionPct}</th>
                      <th style={thGroup} rowSpan={2}></th>
                    </tr>
                    <tr>
                      <th style={thSub}>{t.palletsSent}</th>
                      <th style={thSub}>{t.casesSent}</th>
                      <th style={thSub}>{t.palletsReceived}</th>
                      <th style={thSub}>{t.casesReceived}</th>
                      {isSemifixed ? (
                        <>
                          <th style={thSubR}>{t.priceFSMin}</th>
                          <th style={thSubR}>{t.priceFSMax}</th>
                          <th style={thSubR}>{t.priceFSMidpoint}</th>
                        </>
                      ) : <th style={thSubR}>{t.customerCol}</th>}
                      {isSweetLeaf && <th style={thSubR}>{t.slCol}</th>}
                      <th style={thSubR}>{t.gfCol}</th>
                      <th style={thSubR}>{t.growerCol}</th>
                      <th style={thSubR}>{t.customerCol}</th>
                      {isSweetLeaf && <th style={thSubR}>{t.slCol}</th>}
                      <th style={thSubR}>{t.gfCol}</th>
                      <th style={thSubR}>{t.growerCol}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(function (ln, idx) {
                      return (
                        <ProductLineTableRow
                          key={ln.id || "new-" + idx}
                          ln={ln} idx={idx}
                          isSemifixed={isSemifixed} isSweetLeaf={isSweetLeaf} locked={locked}
                          errors={lineErrors[idx]}
                          onChange={setLineField} onRemove={removeLine} canRemove={lines.length > 1}
                          t={t} productOpts={productOpts}
                        />
                      );
                    })}
                  </tbody>
                </>
              )}
            </table>
          </div>
          {!locked && (
            <button type="button" onClick={addLine} style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 700, width: "100%" }}>{"+ " + t.addProduct}</button>
          )}
        </Section>

        <Section title={t.profitSummary}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
            <ProfitCell title={t.colProfitSL} amount={aggProfits.profitSL} desc={t.profitSLDesc} />
            <ProfitCell title={t.colProfitDiff} amount={aggProfits.profitDiff} desc={t.profitDiffDesc} />
            <ProfitCell title={t.colProfitComm} amount={aggProfits.profitComm} desc={t.profitCommDesc} />
            <ProfitCell title={t.colProfitFinance} amount={aggProfits.profitFinance} desc={t.profitFinanceDesc} />
            <ProfitCell title={t.colProfitTotal} amount={aggProfits.total} desc={t.profitTotalDesc} emphasis />
          </div>
          {aggRealProfits && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#047857", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.realSummary}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                <ProfitCell title={t.colProfitSL} amount={aggRealProfits.profitSL} desc={t.profitSLDesc} />
                <ProfitCell title={t.colProfitDiff} amount={aggRealProfits.profitDiff} desc={t.profitDiffDesc} />
                <ProfitCell title={t.colProfitComm} amount={aggRealProfits.profitComm} desc={t.profitCommDesc} />
                <ProfitCell title={t.colProfitFinance} amount={aggRealProfits.profitFinance} desc={t.profitFinanceDesc} />
                <ProfitCell title={t.colProfitTotal} amount={aggRealProfits.total} desc={t.profitTotalDesc} emphasis />
              </div>
            </div>
          )}
        </Section>
      </div>
    </DraggableModal>
  );
}

/* ======================== MAIN ======================== */
export default function FixedPricingRegistry({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [rows, setRows] = useState([]);
  var [editing, setEditing] = useState(null);
  var [showForm, setShowForm] = useState(false);
  var [toast, setToast] = useState("");
  var [search, setSearch] = useState("");
  var [filterClient, setFilterClient] = useState("");
  var [filterStatus, setFilterStatus] = useState("");
  var [filterArrival, setFilterArrival] = useState("");
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  /* 2026-09-29 (Arturo: "en Fixed Pricing & POs agrega un filtro por
     semana") — multi-select, empty = no restriction, same convention
     as every other ad-hoc filter in this module. */
  var [filterWeeks, setFilterWeeks] = useState([]);
  var [dateFrom, setDateFrom] = useState("");
  var [dateTo, setDateTo] = useState("");
  var [cardFilter, setCardFilter] = useState("");
  var [selected, setSelected] = useState({});
  var [sortCol, setSortCol] = useState("date");
  var [sortDir, setSortDir] = useState("desc");
  var seasons = useMemo(function () { return loadSeasons(); }, []);

  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var aliveRef = useRef(true);
  var rowsRef = useRef([]);
  useEffect(function () { rowsRef.current = rows; }, [rows]);

  /* Source of truth = Supabase `fixed_pos` AND `consignment_pos`
     (Phase 2, 2026-09-18) — loaded together and merged into one
     `rows` array so the rest of this component (filters, sort,
     stats, the outer table) never needs to know there are 2 tables
     underneath. Split back apart by `term` only when writing the
     localStorage caches, so each legacy consumer's own key
     (gfi-fixed-pos / gfi-consignment-pos) only ever contains rows
     shaped the way it expects — never a mixed array. */
  var writeRowsCache = function (next) {
    try {
      var fixedOnly = next.filter(function (r) { return r.term !== "consignment"; });
      var consignOnly = next.filter(function (r) { return r.term === "consignment"; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fixedOnly));
      localStorage.setItem(CONSIGNMENT_STORAGE_KEY, JSON.stringify(consignOnly));
    } catch (e) {}
  };
  var loadBothTables = async function (opts) {
    var results = await Promise.all([
      loadFromSupabase("fixed_pos", opts),
      loadFromSupabase("consignment_pos", opts),
    ]);
    return (results[0] || []).concat(results[1] || []);
  };
  var reload = async function () {
    setError(null);
    try {
      var data = await loadBothTables({ force: true });
      if (aliveRef.current) { setRows(data); writeRowsCache(data); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load POs: " + (e.message || String(e)));
    }
  };
  useEffect(function () {
    aliveRef.current = true;
    setError(null);
    loadBothTables()
      .then(function (data) { if (aliveRef.current) { setRows(data); writeRowsCache(data); } })
      .catch(function (e) { if (aliveRef.current) setError("Could not load POs: " + (e.message || String(e))); });
    return function () { aliveRef.current = false; };
  }, []);

  /* PO Status / Arrival Status (2026-09-18, Arturo's 3-status split)
     are computed live from the linked invoice, never stored on the
     PO itself — same convention as everything else in this app that
     mirrors the invoice (REGLA DE STATUS DE PALLET). Loaded fresh
     from Supabase (never getInvoices()'s localStorage-only mirror,
     which can be empty/stale on a device that never opened Invoices
     this session — same bug class fixed repeatedly elsewhere this
     session) so a fresh reload always shows the real current state. */
  var [invoicesRaw, setInvoicesRaw] = useState(function () { return getInvoices(); });
  var [slInvoicesRaw, setSlInvoicesRaw] = useState([]);
  useEffect(function () {
    loadFromSupabase("invoices")
      .then(function (data) { if (aliveRef.current) setInvoicesRaw(data); })
      .catch(function () {});
    loadFromSupabase("sweetleaf_invoices")
      .then(function (data) { if (aliveRef.current) setSlInvoicesRaw(data); })
      .catch(function () {});
  }, []);
  var slPassingIndex = useMemo(function () { return buildSlPassingIndex(slInvoicesRaw); }, [slInvoicesRaw]);

  var persistSingle = useCallback(async function (rec) {
    /* Routed by the record's OWN term (Phase 2) — every line under a
       PO shares the same header, so all lines of one PO always share
       one term/table; no cross-table split within a single save. */
    await saveToSupabase(tableForTerm(rec.term), rec);
    var cur = rowsRef.current || [];
    var idx = cur.findIndex(function (r) { return r.id === rec.id; });
    var next = idx >= 0 ? cur.map(function (r, i) { return i === idx ? rec : r; }) : [rec].concat(cur);
    setRows(next); writeRowsCache(next);
  }, []);
  var persistDelete = useCallback(async function (id) {
    var cur = rowsRef.current || [];
    var existing = cur.find(function (r) { return r.id === id; });
    await deleteFromSupabase(tableForTerm(existing ? existing.term : "fixed"), id);
    var next = cur.filter(function (r) { return r.id !== id; });
    setRows(next); writeRowsCache(next);
  }, []);

  /* Sync from linked invoice when present. The invoice is the source
     of truth for PU#, PO Status and Arrival Status — all 3 recomputed
     live on every render, never trusted from whatever got persisted
     last save (2026-09-18, Arturo's 3-status split: saleStatus =
     PO Status, computed; arrivalStatus = new, computed, never
     persisted; settlementStatus = the only manual one, untouched
     here). */
  var invoicesById = useMemo(function () {
    var out = {};
    invoicesRaw.forEach(function (inv) { if (inv.id != null) out[String(inv.id)] = inv; });
    return out;
  }, [invoicesRaw]);
  var enriched = useMemo(function () {
    return rows.map(function (r) {
      var live = computeLiveStatuses(r, invoicesById, slPassingIndex);
      var patched = { ...r, saleStatus: live.poStatus, arrivalStatus: live.arrivalStatus };
      /* Everything below this point (Customer/SL/GF/Grower Real
         auto-fill, Pallets/Cases Received auto-fill) is shaped around
         the FIXED pricing model's own fields (priceFS/priceSL/...,
         palletsReceived/casesReceived) — Consignment rows (Phase 2,
         2026-09-18) use a completely different field set (minTR/maxTR/
         cases/etc., see calcConsignment) with no equivalent concept,
         so none of it applies; skip straight to the generic PU# mirror
         for those. */
      if (r.term === "consignment") {
        if (!r.linkedInvoiceId) return patched;
        var invC = invoicesById[String(r.linkedInvoiceId)];
        if (!invC) return patched;
        var invPuC = invC.puNumber != null && invC.puNumber !== "" ? invC.puNumber : r.puNumber;
        return { ...patched, puNumber: invPuC };
      }
      /* Customer After-Sale Real (2026-09-18, Arturo) — only ever
         FILLS a currently-empty field, never overwrites a value
         already saved (whether he typed it himself or it was
         auto-filled on an earlier load and then persisted by a
         normal Save) — "editable por cualquier cosa" means it stays
         his to override, this is just the starting default. */
      if ((r.priceFSReal === "" || r.priceFSReal == null)) {
        var realPrice = resolveRealCustomerPrice(r, live.arrivalStatus, invoicesById, slInvoicesRaw);
        if (realPrice != null) patched.priceFSReal = fmt2(realPrice);
      }
      /* SL/GF After-Sale Real — Four Seasons/SweetLeaf sales ONLY
         (2026-09-18, Arturo, clarifying after PU 17437 only got
         Customer Real: "si es good delivery el dato debiera ser el
         mismo que esta en firm price solo en ventas a four seasons,
         otro status no se popula, se captura manual en todos los
         campos after sale price"). Unlike Customer Real, there's no
         single real-invoice field for these split tiers, so once
         Good Delivery is confirmed the Firm (estimated) SL/GF/Grower
         price is carried over as the starting Real value — same "only
         fills empty, never overwrites" rule as Customer Real. Grower
         Real included here too as of the same day (Arturo changed his
         mind: "cambie de opinion, grower tambien populalo en ventas a
         four seasons si el status es good delivery, claro que todos
         son editables") — still Four Seasons/SweetLeaf only, still
         just a starting default the field stays free to override. */
      if (live.arrivalStatus === "goodDelivery" && isSweetLeafClient(r.clientName)) {
        if ((r.priceSLReal === "" || r.priceSLReal == null) && r.priceSL !== "" && r.priceSL != null) {
          patched.priceSLReal = fmt2(r.priceSL);
        }
        if ((r.priceGFReal === "" || r.priceGFReal == null) && r.priceGF !== "" && r.priceGF != null) {
          patched.priceGFReal = fmt2(r.priceGF);
        }
        if ((r.priceGrowerReal === "" || r.priceGrowerReal == null) && r.priceGrower !== "" && r.priceGrower != null) {
          patched.priceGrowerReal = fmt2(r.priceGrower);
        }
      }
      if (!r.linkedInvoiceId) return patched;
      var inv = invoicesById[String(r.linkedInvoiceId)];
      if (!inv) return patched;
      /* PU# mirrors live from the linked invoice — it's owned by
         the invoice, not the PO. */
      var invPu = inv.puNumber != null && inv.puNumber !== "" ? inv.puNumber : r.puNumber;
      /* Real Pallets/Cases Received (Phase 3 of the merge plan,
         "cantidades originales del pedido y despues lo que en
         realidad se envio") — auto-filled from the linked invoice's
         own matching line the moment it's linked, same "only fills
         empty, never overwrites" rule as the price fields above.
         Unlike price, this isn't gated on Good Delivery: what
         actually shipped is a physical fact recorded at invoicing
         time, not something a later Protection/PAS dispute changes —
         those are about price/condition, never quantity. Always
         reads the GFI invoice's own line, even for SweetLeaf sales —
         the shipped cases/pallets are identical on both sides of the
         mirror, no need to special-case SL here like price does. */
      var patchedQty = { ...patched, puNumber: invPu };
      if ((r.palletsReceived === "" || r.palletsReceived == null) || (r.casesReceived === "" || r.casesReceived == null)) {
        var qtyMatched = matchLineToInvoiceLines(r, inv.lines || []);
        var qtyLine = qtyMatched[0];
        if (qtyLine) {
          if ((r.palletsReceived === "" || r.palletsReceived == null) && qtyLine.pallets !== "" && qtyLine.pallets != null) {
            patchedQty.palletsReceived = qtyLine.pallets;
          }
          if ((r.casesReceived === "" || r.casesReceived == null) && qtyLine.cases !== "" && qtyLine.cases != null) {
            patchedQty.casesReceived = qtyLine.cases;
          }
        }
      }
      return patchedQty;
    });
  }, [rows, invoicesById, slPassingIndex, slInvoicesRaw]);

  /* Window globals — exposed for cross-module use (invoice flow).
     Returns up to 5 PO lines whose date is within ±7 days of the
     requested date and (optionally) match the clientId. Each entry
     has enough data for the invoice form to render a suggestion
     and stamp linkedInvoiceId back when picked. */
  useEffect(function () {
    window.__gfiGetMatchingPOs = function (dateStr, clientId) {
      if (!dateStr) return [];
      var target = new Date(dateStr + "T00:00:00").getTime();
      if (isNaN(target)) return [];
      var sevenDays = 7 * 24 * 3600 * 1000;
      var src = rowsRef.current || [];
      return src.filter(function (r) {
        if (clientId && String(r.clientId) !== String(clientId)) return false;
        if (!r.date) return false;
        var d = new Date(r.date + "T00:00:00").getTime();
        if (isNaN(d)) return false;
        return Math.abs(d - target) <= sevenDays;
      }).slice(0, 5);
    };
    /* Allow the invoice form to push back: stamp linkedInvoiceId +
       priceFSReal onto the matching PO line so the comparison
       columns turn on. */
    window.__gfiLinkInvoiceToPo = async function (poLineId, invoiceId, priceFSReal) {
      var current = rowsRef.current || [];
      var match = current.find(function (r) { return String(r.id) === String(poLineId); });
      if (!match) return;
      var updated = { ...match, linkedInvoiceId: invoiceId || "", priceFSReal: (priceFSReal != null && priceFSReal !== "") ? String(priceFSReal) : match.priceFSReal, updatedAt: new Date().toISOString() };
      try { await persistSingle(updated); } catch (e) { console.error("[FixedPricing] link invoice failed", e); }
    };
    return function () {
      try { delete window.__gfiGetMatchingPOs; delete window.__gfiLinkInvoiceToPo; } catch (e) {}
    };
  }, [persistSingle]);

  /* 2026-10-20, Arturo: "el PO del cliente debe ser link cliqueable que
     abra directamente ese po del modulo de PO's... todo lo que se abre
     desde aqui, al cerrarlo debe regresar a la factura abierta" — same
     open-by-id + return-to-caller pattern BillRegistry.js/
     InvoiceRegistry.js already use for __gfiBillOpen/__gfiInvoiceOpen.
     The caller passes the composite id InvoiceRegistry.js already uses
     for PO linkage ("F:<id>" or "C:<id>", see poStorageKeyFor/poIdFor
     there) — stripped down to the raw row id here since `rows` merges
     both fixed_pos and consignment_pos into one array with globally-
     unique Supabase ids, no need to know which table it came from. */
  var returnModuleRef = useRef(null);
  useEffect(function () {
    window.__gfiFixedPoOpen = function (compositeId) {
      var ret = window.__gfiReturnModule; window.__gfiReturnModule = null;
      var parts = String(compositeId || "").split(":");
      var rawId = parts.length > 1 ? parts.slice(1).join(":") : compositeId;
      var po = (rowsRef.current || []).find(function (r) { return String(r.id) === String(rawId); });
      if (po) { returnModuleRef.current = ret; setEditing(po); setShowForm(true); }
    };
    return function () { delete window.__gfiFixedPoOpen; };
  }, []);

  /* Multi-product PO form (2026-09-18) — one Save persists every
     product line under the PO in one go, plus deletes any line the
     user removed from the form. Each line is still its own
     independent fixed_pos record (Option A, confirmed with Arturo) —
     this just batches the calls so a 3-product PO doesn't need 3
     separate round-trips through the modal. */
  var handleSaveLines = async function (records, deleteIds) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      for (var i = 0; i < (deleteIds || []).length; i++) await persistDelete(deleteIds[i]);
      for (var j = 0; j < records.length; j++) await persistSingle(records[j]);
      setToast(t.saved);
      setEditing(records[0]);
    } catch (e) {
      setError("Could not save PO: " + (e.message || String(e)));
      setToast("⚠ " + (e.message || String(e)));
    } finally { setSaving(false); }
  };
  var handleDeleteLines = async function (ids) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      for (var i = 0; i < ids.length; i++) await persistDelete(ids[i]);
      setToast(t.deleted);
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      setError("Could not delete PO: " + (e.message || String(e)));
      setToast("⚠ " + (e.message || String(e)));
    } finally { setSaving(false); }
  };

  var clients = useMemo(function () { return getClients(); }, []);
  var clientOpts = useMemo(function () {
    var uniq = [...new Set(rows.map(function (r) { return r.clientName; }).filter(Boolean))].sort();
    return uniq.map(function (n) { return { value: n, label: n }; });
  }, [rows]);
  void clients;

  var seasonFilteredRows = useMemo(function () {
    if (filterSeasons.length === 0) return enriched;
    return enriched.filter(function (r) {
      var sid = resolveSeasonId(seasons, r.date, r.seasonId);
      return sid && filterSeasons.indexOf(String(sid)) >= 0;
    });
  }, [enriched, filterSeasons, seasons]);
  /* 2026-09-29 (Arturo: "en Fixed Pricing & POs agrega un filtro por
     semana") — options built from the season-filtered set, not the
     raw `rows` — weeks only make sense to offer for the seasons
     currently in view. Sorted numerically so W9 sorts before W10, not
     lexicographically. */
  var weekOpts = useMemo(function () {
    var uniq = [...new Set(seasonFilteredRows.map(function (r) { return r.week; }).filter(Boolean).map(String))];
    uniq.sort(function (a, b) { return parseInt(a, 10) - parseInt(b, 10); });
    return uniq.map(function (w) { return { value: w, label: "W" + w }; });
  }, [seasonFilteredRows]);

  var filtered = useMemo(function () {
    var list = seasonFilteredRows.slice();
    if (search) {
      var q = search.toLowerCase();
      list = list.filter(function (r) {
        return (r.poNumber || "").toLowerCase().includes(q)
          || (r.puNumber || "").toLowerCase().includes(q)
          || (r.loggerNumber || "").toLowerCase().includes(q)
          || (r.productName || "").toLowerCase().includes(q)
          || (r.productBase || "").toLowerCase().includes(q)
          || (r.clientName || "").toLowerCase().includes(q);
      });
    }
    if (filterClient) list = list.filter(function (r) { return r.clientName === filterClient; });
    if (filterStatus) list = list.filter(function (r) { return (r.saleStatus || "pending") === filterStatus; });
    if (filterArrival) list = list.filter(function (r) { return r.arrivalStatus === filterArrival; });
    if (filterWeeks.length > 0) list = list.filter(function (r) { return r.week && filterWeeks.indexOf(String(r.week)) >= 0; });
    if (dateFrom) list = list.filter(function (r) { return r.date && r.date >= dateFrom; });
    if (dateTo)   list = list.filter(function (r) { return r.date && r.date <= dateTo; });
    if (cardFilter === "pending")  list = list.filter(function (r) { return (r.saleStatus || "pending") === "pending"; });
    if (cardFilter === "settled")  list = list.filter(function (r) { return r.settlementStatus === "settled"; });
    return list;
  }, [seasonFilteredRows, search, filterClient, filterStatus, filterArrival, filterWeeks, dateFrom, dateTo, cardFilter]);

  var sorted = useMemo(function () {
    var list = filtered.slice();
    list.sort(function (a, b) {
      var va, vb;
      switch (sortCol) {
        case "date":         va = a.date || "";         vb = b.date || ""; break;
        case "delivery":     va = a.deliveryDate || ""; vb = b.deliveryDate || ""; break;
        case "week":         va = parseInt(a.week, 10) || 0; vb = parseInt(b.week, 10) || 0; break;
        case "client":       va = (a.clientName || "").toLowerCase(); vb = (b.clientName || "").toLowerCase(); break;
        case "po":           va = a.poNumber || "";     vb = b.poNumber || ""; break;
        case "pu":           va = a.puNumber || "";     vb = b.puNumber || ""; break;
        case "logger":       va = a.loggerNumber || ""; vb = b.loggerNumber || ""; break;
        case "product":      va = (a.productBase || "").toLowerCase(); vb = (b.productBase || "").toLowerCase(); break;
        case "cat":          va = a.category || "";     vb = b.category || ""; break;
        case "palletsSent":  va = palletsFor(a); vb = palletsFor(b); break;
        case "cases":        va = casesFor(a); vb = casesFor(b); break;
        case "palletsRcv":   va = parseFloat(a.palletsReceived) || 0; vb = parseFloat(b.palletsReceived) || 0; break;
        case "casesRcv":     va = parseInt(a.casesReceived, 10) || 0; vb = parseInt(b.casesReceived, 10) || 0; break;
        case "priceFS":      va = customerPriceFor(a); vb = customerPriceFor(b); break;
        case "priceSL":      va = parseFloat(a.priceSL) || 0; vb = parseFloat(b.priceSL) || 0; break;
        case "priceGF":      va = parseFloat(a.priceGF) || 0; vb = parseFloat(b.priceGF) || 0; break;
        case "priceGrower":  va = parseFloat(a.priceGrower) || 0; vb = parseFloat(b.priceGrower) || 0; break;
        case "priceFSReal":     va = parseFloat(a.priceFSReal) || 0;     vb = parseFloat(b.priceFSReal) || 0; break;
        case "priceSLReal":     va = parseFloat(a.priceSLReal) || 0;     vb = parseFloat(b.priceSLReal) || 0; break;
        case "priceGFReal":     va = parseFloat(a.priceGFReal) || 0;     vb = parseFloat(b.priceGFReal) || 0; break;
        case "priceGrowerReal": va = parseFloat(a.priceGrowerReal) || 0; vb = parseFloat(b.priceGrowerReal) || 0; break;
        case "commPct":      va = parseFloat(a.commissionPct) || 0;     vb = parseFloat(b.commissionPct) || 0; break;
        case "commPctReal":  va = parseFloat(a.commissionPctReal) || 0; vb = parseFloat(b.commissionPctReal) || 0; break;
        case "profitSL":      va = profitFor(a, false).profitSL;      vb = profitFor(b, false).profitSL; break;
        case "profitDiff":    va = profitFor(a, false).profitDiff;    vb = profitFor(b, false).profitDiff; break;
        case "profitComm":    va = profitFor(a, false).profitComm;    vb = profitFor(b, false).profitComm; break;
        case "profitFinance": va = profitFor(a, false).profitFinance; vb = profitFor(b, false).profitFinance; break;
        case "profit":       va = profitFor(a, false).total; vb = profitFor(b, false).total; break;
        case "profitReal":   va = lineHasRealAny(a) ? profitFor(a, true).total : -Infinity; vb = lineHasRealAny(b) ? profitFor(b, true).total : -Infinity; break;
        case "status":       va = a.saleStatus || ""; vb = b.saleStatus || ""; break;
        case "arrivalStatus":    va = a.arrivalStatus || "";    vb = b.arrivalStatus || ""; break;
        case "settlementStatus": va = a.settlementStatus || ""; vb = b.settlementStatus || ""; break;
        default: va = ""; vb = "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortCol, sortDir]);

  var toggleSort = function (col) {
    if (sortCol === col) { setSortDir(sortDir === "asc" ? "desc" : "asc"); return; }
    setSortCol(col); setSortDir("desc");
  };
  var arrow = function (col) {
    if (sortCol !== col) return <span style={{ marginLeft: 3, color: "var(--color-text-muted)", opacity: 0.4, fontSize: 9 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: "var(--color-accent)", fontSize: 9 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  /* Summary card totals — over the FILTERED set so the cards
     reflect what the user is looking at, not the raw DB. */
  /* Reads from seasonFilteredRows (not `filtered`) so clicking the
     pending/settled cards doesn't collapse the OTHER card's count
     against itself — same isolation pattern as BillRegistry/
     InvoiceRegistry/PaymentRegistry. */
  var stats = useMemo(function () {
    var lines = seasonFilteredRows.length;
    var cases = 0, revenue = 0, profit = 0;
    var pending = 0, settled = 0;
    seasonFilteredRows.forEach(function (r) {
      var c = casesFor(r);
      cases += c;
      revenue += customerPriceFor(r) * c;
      profit += profitFor(r, false).total;
      if ((r.saleStatus || "pending") === "pending") pending++;
      if (r.settlementStatus === "settled") settled++;
    });
    return { lines: lines, cases: cases, revenue: revenue, profit: profit, pending: pending, settled: settled };
  }, [seasonFilteredRows]);

  /* Aggregator used by both the "selected" bar block and the
     module-total block to the right. Walks a list of rows and rolls
     up the columns the bar exposes: Revenue (FS × cases — Real when
     available), Cases, Diff Profit, Comm Profit, Est. Profit (sum
     of all three Est. components), and Real Profit when the row
     has a Real FS price stamped from the linked invoice. */
  var aggregateRows = useCallback(function (list) {
    var out = { count: 0, cases: 0, revenue: 0, profitDiff: 0, profitComm: 0, profitFinance: 0, estProfit: 0, realProfit: 0 };
    (list || []).forEach(function (r) {
      var cases = casesFor(r);
      var p = profitFor(r, false);
      var hasReal = lineHasRealAny(r);
      var fsForRevenue = r.term === "consignment"
        ? customerPriceFor(r)
        : ((r.priceFSReal !== "" && r.priceFSReal != null && !isNaN(parseFloat(r.priceFSReal))) ? parseFloat(r.priceFSReal) : (parseFloat(r.priceFS) || 0));
      out.count        += 1;
      out.cases        += cases;
      out.revenue      += fsForRevenue * cases;
      out.profitDiff   += p.profitDiff;
      out.profitComm   += p.profitComm;
      out.profitFinance+= p.profitFinance;
      out.estProfit    += p.total;
      if (hasReal) out.realProfit += profitFor(r, true).total;
    });
    return out;
  }, []);

  var selectedRows = useMemo(function () { return filtered.filter(function (r) { return selected[r.id]; }); }, [filtered, selected]);
  /* Export rows — selected if any checkboxes, otherwise the full
     filtered set. Column order mirrors the visible table so the
     spreadsheet reads like a copy of what the user sees. Computed
     profit columns are flattened to numbers so Excel treats them
     as real currency (sortable / summable). */
  var buildExportRows = function () {
    var src = selectedRows.length > 0 ? selectedRows : filtered;
    return src.map(function (r) {
      var p = profitFor(r, false);
      var hasReal = lineHasRealAny(r);
      var rp = hasReal ? profitFor(r, true) : null;
      return {
        "Loading/Delivery Date": xs(r.deliveryDate),
        "Departure Date": xs(r.date),
        "Week": xs(r.week ? "W" + r.week : ""),
        "Client": xs(r.clientName),
        "PO #": xs(r.poNumber),
        "PU #": xs(r.puNumber),
        "Logger #": xs(r.loggerNumber),
        "Product": xs(r.productDescription || r.productBase),
        "Category": xs(r.category),
        "Pallets Sent": intOr(r.palletsSent),
        "Cases Sent": intOr(r.casesSent),
        "Pallets Received": intOr(r.palletsReceived),
        "Cases Received": intOr(r.casesReceived),
        "Price FS": num2(r.priceFS),
        "Price SL": num2(r.priceSL),
        "Price GF": num2(r.priceGF),
        "Price Grower": num2(r.priceGrower),
        "FS Real": num2(r.priceFSReal),
        "SL Real": num2(r.priceSLReal),
        "GF Real": num2(r.priceGFReal),
        "Grower Real": num2(r.priceGrowerReal),
        "Commission %": num2(r.commissionPct),
        "Real Commission %": num2(r.commissionPctReal),
        "Finance Paid %": num2(r.financeFeePaid),
        "Finance Charged %": num2(r.financeFeeCharged),
        "Real Finance Paid $": num2(r.financeFeePaidReal),
        "Real Finance Charged $": num2(r.financeFeeChargedReal),
        "SL Profit": num2(p.profitSL),
        "Diff Profit": num2(p.profitDiff),
        "Comm Profit": num2(p.profitComm),
        "Finance Profit": num2(p.profitFinance),
        "Est. Profit": num2(p.total),
        "Real Profit": rp ? num2(rp.total) : "",
        "PO Status": xs(r.saleStatus),
        "Arrival Status": xs(r.arrivalStatus),
        "Sale Status": xs(r.settlementStatus),
      };
    });
  };
  var selectedTotals = useMemo(function () { return aggregateRows(selectedRows); }, [selectedRows, aggregateRows]);
  var grandTotals = useMemo(function () { return aggregateRows(filtered); }, [filtered, aggregateRows]);
  var clearSelection = function () { setSelected({}); };
  /* Render a single money amount with the standard color convention:
     positive in green, negative in red wrapped in parentheses, zero
     muted. Matches BillRegistry / InvoiceRegistry. */
  var moneyCell = function (n, opts) {
    var v = parseFloat(n) || 0;
    var neg = v < 0;
    var color = neg ? "#DC2626" : (v > 0 ? (opts && opts.emphasize ? "var(--color-accent)" : "#047857") : "var(--color-text-muted)");
    var txt = neg ? "(" + fmtMoney(Math.abs(v)) + ")" : fmtMoney(v);
    return <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: color }}>{txt}</span>;
  };
  var selectedCount = selectedRows.length;

  /* Compact mode — `\n` in column labels renders as a real 2-line
     wrap (whiteSpace: pre-line) so wide tables fit on screen
     without horizontal scroll. */
  var th = { padding: "5px 4px", textAlign: "left", fontWeight: 700, fontSize: 9, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", background: "var(--color-bg)", position: "sticky", top: 0, zIndex: 1, cursor: "pointer", whiteSpace: "pre-line", lineHeight: 1.15, verticalAlign: "bottom", borderBottom: "2px solid var(--color-border)" };
  var thR = { ...th, textAlign: "right" };
  var thC = { ...th, textAlign: "center" };
  var td = { padding: "5px 4px", fontSize: 10, borderBottom: "1px solid var(--color-border)", whiteSpace: "nowrap" };
  var tdR = { ...td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" };
  var tdC = { ...td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" };
  /* Every column header is now clickable-to-sort (2026-09-18, Arturo:
     "necesito la tabla de ordenes pueda ordenar todas las columnas") —
     this one helper replaces what used to be only ~9 of the ~30
     columns wired with their own onClick+arrow(). */
  var sortableTh = function (col, label, style) {
    return <th onClick={function () { toggleSort(col); }} style={style || th}>{label}{arrow(col)}</th>;
  };

  var renderCard = function (key, label, value, color) {
    var active = cardFilter === key;
    return (
      <div key={key} onClick={function () { setCardFilter(active ? "" : key); }} style={{ padding: 12, borderRadius: 8, border: "1px solid " + (active ? color : "var(--color-border)"), background: active ? "rgba(42,122,111,0.05)" : "var(--color-bg-card)", cursor: "pointer", transition: "all 0.15s" }}>
        <div style={{ fontSize: 10, color: color, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: color, marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <ExportButton lang={lang} filenameBase="Fixed_Pricing_POs" selectedCount={selectedRows.length} totalCount={filtered.length} buildSummary={buildExportRows} buildDetail={buildExportRows} />
          <button onClick={function () { setEditing(null); setShowForm(true); }} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{t.add}</button>
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={function() { setError(null); reload(); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #991B1B", background: "transparent", color: "#991B1B", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button>
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 14 }}>
        {renderCard("lines",   t.cardLines,   stats.lines,                "var(--color-accent)")}
        {renderCard("cases",   t.cardCases,   stats.cases.toLocaleString(),     "#2563EB")}
        {renderCard("revenue", t.cardRevenue, fmtMoney(stats.revenue),    "#0F766E")}
        {renderCard("profit",  t.cardProfit,  fmtMoney(stats.profit),     "#047857")}
        {renderCard("pending", t.cardPending, stats.pending,              "#B45309")}
        {renderCard("settled", t.cardSettled, stats.settled,              "#15803D")}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, maxWidth: 280, flex: "1 1 200px" }} />
        <div style={{ minWidth: 170 }}><SearchSelect value={filterClient} onChange={setFilterClient} placeholder={t.allClients} options={clientOpts} /></div>
        <div style={{ minWidth: 170 }}><SearchSelect value={filterStatus} onChange={setFilterStatus} placeholder={t.allStatuses} options={SALE_STATUSES.map(function (s) { return { value: s, label: t["status" + s.charAt(0).toUpperCase() + s.slice(1)] }; })} /></div>
        <div style={{ minWidth: 170 }}><SearchSelect value={filterArrival} onChange={setFilterArrival} placeholder={t.arrivalStatus} options={ARRIVAL_STATUSES.map(function (s) { return { value: s, label: t["status" + s.charAt(0).toUpperCase() + s.slice(1)] }; })} /></div>
        <div style={{ minWidth: 180 }}><SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} /></div>
        <div style={{ minWidth: 150 }}><SearchSelect multi value={filterWeeks} onChange={setFilterWeeks} placeholder={lang === "es" ? "Todas las semanas" : "All weeks"} options={weekOpts} /></div>
        <input type="date" value={dateFrom} onChange={function (e) { setDateFrom(e.target.value); }} style={{ ...inp, maxWidth: 140 }} title={lang === "es" ? "Desde" : "From"} />
        <input type="date" value={dateTo}   onChange={function (e) { setDateTo(e.target.value); }} style={{ ...inp, maxWidth: 140 }} title={lang === "es" ? "Hasta" : "To"} />
        {(dateFrom || dateTo || filterClient || filterStatus || filterArrival || filterWeeks.length > 0 || cardFilter || search) && (
          <button onClick={function () { setSearch(""); setFilterClient(""); setFilterStatus(""); setFilterArrival(""); setFilterWeeks([]); setDateFrom(""); setDateTo(""); setCardFilter(""); }} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        )}
      </div>

      {/* Selection summary bar — shown only when ≥1 row checked.
          Left: selected-rows aggregates. Right: module-total
          aggregates over the whole filtered set. Mirrors the
          design pattern used in BillRegistry / InvoiceRegistry. */}
      {selectedCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", marginBottom: 10, fontSize: 12 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={async function () {
              if (saving) return;
              var msg = lang === "es" ? "¿Eliminar " + selectedCount + " línea(s) seleccionada(s)?" : "Delete " + selectedCount + " selected line(s)?";
              if (!window.confirm(msg)) return;
              setSaving(true); setError(null);
              try {
                var toDelete = rows.filter(function (r) { return selected[r.id]; });
                for (var i = 0; i < toDelete.length; i++) { await persistDelete(toDelete[i].id); }
                setSelected({});
                setToast(t.deleted);
              } catch (e) {
                setError("Could not delete: " + (e.message || String(e)));
                setToast("⚠ " + (e.message || String(e)));
              } finally { setSaving(false); }
            }} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"🗑 "}{lang === "es" ? "Eliminar Seleccionados" : "Delete Selected"}</button>
            <button onClick={clearSelection} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
          </div>
          {/* CENTER — selected aggregates */}
          <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 14, flexWrap: "wrap", fontSize: 11 }}>
            <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Venta" : "Revenue"}: </span>{moneyCell(selectedTotals.revenue)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Cajas" : "Cases"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases.toLocaleString()}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Difer." : "Diff Profit"}: </span>{moneyCell(selectedTotals.profitDiff)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Com." : "Comm Profit"}: </span>{moneyCell(selectedTotals.profitComm)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Fin." : "Finance Profit"}: </span>{moneyCell(selectedTotals.profitFinance)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Est." : "Est. Profit"}: </span>{moneyCell(selectedTotals.estProfit, { emphasize: true })}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Real" : "Real Profit"}: </span>{moneyCell(selectedTotals.realProfit, { emphasize: true })}</span>
          </div>
          {/* RIGHT — module total over the filtered set */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, minWidth: 240, paddingLeft: 14, borderLeft: "1px dashed var(--color-border)" }}>
            <div style={{ fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Total módulo" : "Module total"}</div>
            <div style={{ display: "flex", gap: 8, fontSize: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <span title={lang === "es" ? "Venta" : "Revenue"}>{moneyCell(grandTotals.revenue)}</span>
              <span title={lang === "es" ? "Cajas" : "Cases"} style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{grandTotals.cases.toLocaleString()}c</span>
              <span title={lang === "es" ? "Gan. Difer." : "Diff Profit"}>{moneyCell(grandTotals.profitDiff)}</span>
              <span title={lang === "es" ? "Gan. Com." : "Comm Profit"}>{moneyCell(grandTotals.profitComm)}</span>
              <span title={lang === "es" ? "Gan. Fin." : "Finance Profit"}>{moneyCell(grandTotals.profitFinance)}</span>
              <span title={lang === "es" ? "Gan. Est." : "Est. Profit"}>{moneyCell(grandTotals.estProfit, { emphasize: true })}</span>
              <span title={lang === "es" ? "Gan. Real" : "Real Profit"}>{moneyCell(grandTotals.realProfit, { emphasize: true })}</span>
            </div>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14, border: "1px solid var(--color-border)", borderRadius: 10 }}>{t.noResults}</div>
      ) : (
        <div style={{ overflowX: "auto", maxHeight: "calc(100vh - 360px)", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...thC, width: 32 }}>
                  <input type="checkbox" checked={sorted.length > 0 && sorted.every(function (r) { return selected[r.id]; })} onChange={function (e) {
                    var next = { ...selected };
                    if (e.target.checked) sorted.forEach(function (r) { next[r.id] = true; });
                    else sorted.forEach(function (r) { delete next[r.id]; });
                    setSelected(next);
                  }} />
                </th>
                {/* Date columns first per spec: Loading/Delivery,
                    Departure, Week — then the rest. Every column is
                    sortable now (2026-09-18, Arturo). */}
                {sortableTh("delivery", t.colDelivery)}
                {sortableTh("date", t.colDate)}
                {sortableTh("week", t.colWeek, thC)}
                {sortableTh("client", t.colClient)}
                {sortableTh("po", t.colPO)}
                {sortableTh("pu", t.colPU)}
                {sortableTh("logger", t.colLogger)}
                {sortableTh("product", t.colProduct)}
                {sortableTh("cat", t.colCat, thC)}
                {sortableTh("palletsSent", t.colPalletsSent, thR)}
                {sortableTh("cases", t.colCasesSent, thR)}
                {sortableTh("palletsRcv", t.colPalletsRcv, thR)}
                {sortableTh("casesRcv", t.colCasesRcv, thR)}
                {sortableTh("priceFS", t.colPriceFS, thR)}
                {sortableTh("priceSL", t.colPriceSL, thR)}
                {sortableTh("priceGF", t.colPriceGF, thR)}
                {sortableTh("priceGrower", t.colPriceGrower, thR)}
                {sortableTh("priceFSReal", t.colPriceFSReal, thR)}
                {sortableTh("priceSLReal", t.colPriceSLReal, thR)}
                {sortableTh("priceGFReal", t.colPriceGFReal, thR)}
                {sortableTh("priceGrowerReal", t.colPriceGrowerReal, thR)}
                {sortableTh("commPct", t.colCommPct, thR)}
                {sortableTh("commPctReal", t.colCommPctReal, thR)}
                {sortableTh("profitSL", t.colProfitSL, thR)}
                {sortableTh("profitDiff", t.colProfitDiff, thR)}
                {sortableTh("profitComm", t.colProfitComm, thR)}
                {sortableTh("profitFinance", t.colProfitFinance, thR)}
                {sortableTh("profit", t.colProfitTotal, thR)}
                {sortableTh("profitReal", t.colProfitReal, thR)}
                {sortableTh("status", t.colPoStatus, thC)}
                {sortableTh("arrivalStatus", t.colArrivalStatus, thC)}
                {sortableTh("settlementStatus", t.colSaleStatus, thC)}
              </tr>
            </thead>
            <tbody>
              {sorted.map(function (r) {
                var p = profitFor(r, false);
                var rp = lineHasRealAny(r) ? profitFor(r, true) : null;
                var openRow = function () { setEditing(r); setShowForm(true); };
                return (
                  <tr key={r.id} onClick={openRow} style={{ cursor: "pointer", background: selected[r.id] ? "rgba(42,122,111,0.05)" : "transparent" }}
                      onMouseEnter={function (e) { if (!selected[r.id]) e.currentTarget.style.background = "var(--color-bg)"; }}
                      onMouseLeave={function (e) { if (!selected[r.id]) e.currentTarget.style.background = "transparent"; }}>
                    <td style={{ ...tdC, width: 32 }} onClick={function (e) { e.stopPropagation(); }}>
                      <input type="checkbox" checked={!!selected[r.id]} onChange={function (e) { var next = { ...selected }; if (e.target.checked) next[r.id] = true; else delete next[r.id]; setSelected(next); }} />
                    </td>
                    {/* Loading/Delivery first, Departure second, Week. */}
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.deliveryDate || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.date || "—"}</td>
                    <td style={tdC}>{r.week ? "W" + r.week : "—"}</td>
                    <td style={td}>{r.clientName || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{r.poNumber || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.puNumber || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.loggerNumber || "—"}</td>
                    <td style={td}>{r.productDescription || r.productBase || "—"}</td>
                    <td style={tdC}>{r.category || "—"}</td>
                    <td style={tdR}>{r.palletsSent || "—"}</td>
                    <td style={{ ...tdR, fontWeight: 700 }}>{r.casesSent || "—"}</td>
                    <td style={tdR}>{r.palletsReceived || "—"}</td>
                    <td style={tdR}>{r.casesReceived || "—"}</td>
                    <td style={tdR}>{fmtMoneyShort(r.priceFS)}</td>
                    <td style={tdR}>{fmtMoneyShort(r.priceSL)}</td>
                    <td style={tdR}>{fmtMoneyShort(r.priceGF)}</td>
                    <td style={tdR}>{fmtMoneyShort(r.priceGrower)}</td>
                    <td style={{ ...tdR, color: r.priceFSReal ? "#047857" : "var(--color-text-muted)", fontWeight: r.priceFSReal ? 700 : 400 }}>{r.priceFSReal ? fmtMoneyShort(r.priceFSReal) : "—"}</td>
                    <td style={{ ...tdR, color: r.priceSLReal ? "#047857" : "var(--color-text-muted)", fontWeight: r.priceSLReal ? 700 : 400 }}>{r.priceSLReal ? fmtMoneyShort(r.priceSLReal) : "—"}</td>
                    <td style={{ ...tdR, color: r.priceGFReal ? "#047857" : "var(--color-text-muted)", fontWeight: r.priceGFReal ? 700 : 400 }}>{r.priceGFReal ? fmtMoneyShort(r.priceGFReal) : "—"}</td>
                    <td style={{ ...tdR, color: r.priceGrowerReal ? "#047857" : "var(--color-text-muted)", fontWeight: r.priceGrowerReal ? 700 : 400 }}>{r.priceGrowerReal ? fmtMoneyShort(r.priceGrowerReal) : "—"}</td>
                    <td style={tdR}>{r.commissionPct != null && r.commissionPct !== "" ? r.commissionPct + "%" : "—"}</td>
                    <td style={{ ...tdR, color: (r.commissionPctReal !== "" && r.commissionPctReal != null) ? "#047857" : "var(--color-text-muted)", fontWeight: (r.commissionPctReal !== "" && r.commissionPctReal != null) ? 700 : 400 }}>{(r.commissionPctReal !== "" && r.commissionPctReal != null) ? (r.commissionPctReal + "%") : "—"}</td>
                    <td style={{ ...tdR, color: p.profitSL >= 0 ? "#047857" : "#B91C1C" }}>{fmtMoneyShort(p.profitSL)}</td>
                    <td style={{ ...tdR, color: p.profitDiff >= 0 ? "#047857" : "#B91C1C" }}>{fmtMoneyShort(p.profitDiff)}</td>
                    <td style={{ ...tdR, color: p.profitComm >= 0 ? "#047857" : "#B91C1C" }}>{fmtMoneyShort(p.profitComm)}</td>
                    <td style={{ ...tdR, color: p.profitFinance >= 0 ? "#047857" : "#B91C1C" }}>{fmtMoneyShort(p.profitFinance)}</td>
                    <td style={{ ...tdR, fontWeight: 700, color: p.total >= 0 ? "var(--color-accent)" : "#B91C1C" }}>{fmtMoneyShort(p.total)}</td>
                    <td style={{ ...tdR, fontWeight: 700, color: rp ? "#047857" : "var(--color-text-muted)" }}>{rp ? fmtMoneyShort(rp.total) : "—"}</td>
                    <td style={tdC}><StatusBadge status={r.saleStatus || "pending"} t={t} /></td>
                    <td style={tdC}>{r.arrivalStatus ? <StatusBadge status={r.arrivalStatus} t={t} /> : <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>
                    <td style={tdC}>{r.settlementStatus ? <StatusBadge status={r.settlementStatus} t={t} /> : <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <PoLineModal line={editing} allRows={enriched} t={t} lang={lang} onSaveLines={handleSaveLines} onDeleteLines={handleDeleteLines} onClose={function () { setShowForm(false); setEditing(null); if (returnModuleRef.current && window.__gfiNavigate) window.__gfiNavigate(returnModuleRef.current); returnModuleRef.current = null; }} />}
      <Toast msg={toast} onDismiss={function () { setToast(""); }} />
    </div>
  );
}
